import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import AddIcon from '../assets/icons/add.svg';
import Filter from '../components/filter/Filter';
import { getClientOptions, getClientProjects } from '../api/client';
import { getAllInfo, userCanCreateNewOrder } from '../api/drapery-order';
import { archiveProjects, deleteProject, getProjects, updateProject } from '../api/projects';
import moment from 'moment';
import { FadeLoader } from 'react-spinners';
import ConfirmModal from '../modals/confirm_modal';
import { SEARCH_INPUT_DEBOUNCE_MS, useDebounce, useIsMobile } from '../helper/customHooks';
import { getProjectStatuses } from '../api/statuses';
import { initial_project_statuses } from '../initials/initials';
import { trackView } from '../components/analytics/track_view';
import { MixpanelEvents } from '../components/analytics/mix_panel_events';
import {
  buyAdditionalSeats,
  changeSubscriptionPlan,
  getAccount,
  getSeatInfo,
  updateAccount,
} from '../api/account';
import { ClientDashboardModule, WorkOrderModule } from '../redux';
import { editWorkOrder, getWorkOrderPDFContent } from '../api/work-order';
import { callLambda, getPdfFileNameFromLambdaResponse } from '../api/lambda';
import {
  getMergedPdfPrintUrl,
  isMergePdfNoneFoundError,
  mergePdfFiles,
  storeFile,
} from '../api/file-store';
import PrintModal from '../modals/PrintModal';
import styled from 'styled-components';
import { Config } from '../utils';
import { checkGate } from '../statsig';
import {
  openQuipaAssistant,
  QUIPA_ASSISTANT_CREATE_PROJECT_GATE,
  QUIPA_ASSISTANT_CLOSED_EVENT,
} from '../components/projects/quipa-assistant-events';
import { Button } from '../components/ui/button';
import { InputGroup, InputGroupAddon, InputGroupInput } from '../components/UI/input-group';
import {
  ArrowDownIcon,
  AtSign,
  CircleCheck,
  CirclePlus,
  ClipboardList,
  CreditCard,
  LucideChevronDown,
  LucideX,
  Mail,
  Search,
  TriangleAlert,
  UserRound,
} from 'lucide-react';
import FiltersDrawer from '../components/filters_drawer/FiltersDrawer';
import { DataTable } from '../components/data-table/DataTable';
// TODO: Asked to be removed in task 2472
// import { Checkbox } from '../components/ui/checkbox';
import { DropdownCell } from '../components/data-table/DropdownCell';
import ComboCell from '../components/data-table/ComboCell';
import CustomColumnTextCell from '../components/orderingAndReceiving/custom_column_text_cell';
// import NoteCell from '../components/data-table/NoteCell';
import DotMenuCell from '../components/data-table/DotMenuCell';
import ColumnVisibilityMenu from '../components/data-table/column_visibility_menu';
import {
  DEFAULT_PROJECT_OVERVIEW_COLUMN_VISIBILITY,
  PROJECT_OVERVIEW_COLUMNS,
  ProjectOverviewColumnKey,
  ProjectOverviewColumnVisibility,
  resolveColumnVisibility,
} from '../components/data-table/column_visibility';
import DateCell from '../components/data-table/DateCell';
import { handleUpdateLastViewedProject } from '../helper/apiFunctions';
import Notebook from '../components/notebook/Notebook';
import EstimateStatusCell from '../components/estimating/EstimateStatusCell';
import EstimateBuilderSheet from '../components/estimating/EstimateBuilderSheet';
import {
  COLUMN_VISIBILITY_PAGES,
  normalizeAssignmentColumnVisibility,
  normalizeColumnVisibilityPreference,
} from '../helper/columnVisibilityPreference';
import { readCachedPricingPermission } from '../helper/pricingPermission';
import { getAllEstimates } from '../api/estimates';
import { quickbooksCheckIsConnected } from '../api/quickbooks';
import type { ProjectEstimate } from '../interfaces/project-estimate';
import RoleAssignmentDropdown from '../components/role_assignment/role_assignment_dropdown';
import { getAllRolesByAccountId } from '../api/roles';
import {
  CustomColumnPreference,
  getColumnVisibilityPreference,
  saveCustomColumnsPreference,
  upsertColumnVisibilityPreference,
} from '../api/column-visibility-preferences';
import { addNewAssignee, editAssignee, getAllAssigneesByAccountId } from '../api/assignees';
import { getUser, inviteMember } from '../api/users';
import {
  applyRoleAssignmentSelection,
  buildRoleAssignmentFilterOptions,
  buildRoleAssignmentOptionsByRoleId,
  extractAssigneesForRoleAssignments,
  extractTeamMembersForRoleAssignments,
  getRoleAssignmentSelectedOptionKey,
  getRoleColumnsForTarget,
  normalizeRoleAssignments,
  resolveRoleAssignmentOwnerAccountId,
  type RoleAssignmentOptionsByRoleId,
  type RoleAssignmentRole,
} from '../helper/roleAssignments';
import WideSheet from '../components/wide_sheet/WideSheet';
import {
  AssigneeWithoutLoginRow,
  collectUsedLabels,
  extractApiErrorMessage,
  extractSeatLimitFromPlanInfo,
  formatTimeAgo,
  generateUniqueAssigneeLabel,
  InvitePanelMode,
  mapAssigneePayloadToRow,
  normalizeAssigneeLabelInput,
  normalizeRoleIds,
  PendingInvitationRow,
  PermissionTooltipKey,
  PRICING_TABLE_PLANS_GATE,
  readOwnerAccountIdFromStorage,
  resolveOwnerAccountIdFromTeamContext,
  RolePermissionRow,
  showErrorToast,
  showSuccessToast,
  showToast,
  TablePermissionTooltipPosition,
  TeamMemberPermissionDraft,
  TeamMemberRow,
} from './team_settings';
import { getCustomerPlanInfo } from '../api/stripe';
import UserCheckIcon from '../assets/icons/user-check.svg';
import { Input } from '../components/ui/input';
import { Checkbox } from '../components/ui/checkbox-shadcn';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { RadioGroup, RadioGroupItem } from '../components/ui/radio-group';
import { Label } from '../components/ui/label';
import PermissionHelpTooltip from '../components/permission_help_tooltip';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/UI/dropdown-menu';
import {
  CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID,
  isConfiguredCustomColumn,
  normalizeCustomColumnsConfig,
  readCustomColumnValue,
  writeCustomColumnValue,
  type CustomColumnId,
} from '../components/orderingAndReceiving/custom_columns';
import { Tooltip, TooltipContent, TooltipTrigger } from '../components/ui/tooltip-shadcn';

const GeneratePdfSpinner = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 100000000;
`;

const releaseDate = new Date('2026-01-12');
const thirtyDaysAfterRelease = new Date(releaseDate);
thirtyDaysAfterRelease.setDate(thirtyDaysAfterRelease.getDate() + 30);

const showNewBadge = thirtyDaysAfterRelease > new Date();
const assigneeColumnsReleaseDate = new Date('2026-03-02');
const thirtyDaysAfterAssigneeColumnsRelease = new Date(assigneeColumnsReleaseDate);
thirtyDaysAfterAssigneeColumnsRelease.setDate(thirtyDaysAfterAssigneeColumnsRelease.getDate() + 30);
const showAssigneeColumnsNewBadge = thirtyDaysAfterAssigneeColumnsRelease > new Date();

const areRoleAssignmentColumnsEqual = (
  currentColumns: RoleAssignmentRole[],
  nextColumns: RoleAssignmentRole[]
) => {
  if (currentColumns.length !== nextColumns.length) {
    return false;
  }

  return currentColumns.every((currentRole, index) => {
    const nextRole = nextColumns[index];

    return (
      currentRole?.id === nextRole?.id &&
      currentRole?.name === nextRole?.name &&
      currentRole?.projects === nextRole?.projects
    );
  });
};

const PROJECTS_COLUMN_KEYS = PROJECT_OVERVIEW_COLUMNS.map((column) => column.key);

const resolveProjectsColumnVisibility = (
  visibility?: ProjectOverviewColumnVisibility
): ProjectOverviewColumnVisibility => ({
  ...resolveColumnVisibility(visibility),
  estimate: true,
});

let initialSeatState = {
  interval: 'month',
  quantity: '1',
  cost: '0.00',
};

const ProjectsPage = (props: any) => {
  // ####### LOCAL STORAGE #######
  let accountId = parseFloat(localStorage.getItem('accountId') || '0');
  // ####### END OF LOCAL STORAGE #######

  // ####### REFS #######
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const searchInputRef = useRef<any>();
  const submenuRef = useRef<any>();
  const confirmModalRef = useRef<any>();
  // ####### END OF REFS #######

  // ####### STATES #######
  const firstName = (localStorage.getItem('firstName') || '').trim();
  const lastName = (localStorage.getItem('lastName') || '').trim();
  const isMobile = useIsMobile(800);
  const [filters, setFilters] = useState<any>({
    client: { value: [] },
    status: { value: [] },
    estimate: { value: [] },
    invoice: { value: [] },
    assignee: { value: [] },
    lastViewedAt: { value: [] },
  });
  const [clients, setClients] = useState<any[]>([]);
  const [isSubmenuOpened, setIsSubmenuOpened] = useState<boolean>(false);
  const [tableData, setTableData] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [refetch, setRefetch] = useState<boolean>(false);
  const [refetchRoles, setRefetchRoles] = useState<boolean>(false);
  const [refetchProjectStatuses, setRefetchProjectStatuses] = useState<boolean>(false);
  const [openConfirmationModal, setOpenConfirmationModal] = useState<any>({
    active: false,
    action: '',
  });
  const [selectedRows, setSelectedRows] = useState<any[]>([]);
  const [itemsAmount, setItemsAmount] = useState<number>(0);
  const [sort, setSort] = useState<any>({
    column: 'lastViewedAt',
    value: 'DESC',
    position: 2,
  });
  const [searchTerm, setSearchTerm] = useState<any>('');
  const [searchSubmitTrigger, setSearchSubmitTrigger] = useState(0);
  const debouncedSearch = useDebounce(searchTerm, SEARCH_INPUT_DEBOUNCE_MS, searchSubmitTrigger);
  const [projectStatuses, setProjectStatuses] = useState<any[]>(initial_project_statuses);
  const [remainingItemOrder, setRemainingItemOrder] = useState<number>(0);
  const [account, setAccount] = useState<any>(null);
  const [accountIsAdmin, setAccountIsAdmin] = useState<boolean>(false);
  const [accountCanSeePricing, setAccountCanSeePricing] = useState<boolean>(
    readCachedPricingPermission(false)
  );
  const [estimateSheetState, setEstimateSheetState] = useState<{
    open: boolean;
    estimateId: number | null;
    client: any | null;
    estimates: ProjectEstimate[];
    qboConnected: boolean;
  }>({
    open: false,
    estimateId: null,
    client: null,
    estimates: [],
    qboConnected: false,
  });
  const [isPrintModalOpen, setIsPrintModalOpen] = useState<boolean>(false);
  const [projectList, setProjectList] = useState<ClientDashboardModule.ClientProject[]>([]);
  const [generatePdf, setGeneratePdf] = useState<boolean>(false);
  const [columnVisibility, setColumnVisibility] = useState<ProjectOverviewColumnVisibility>(
    resolveProjectsColumnVisibility(DEFAULT_PROJECT_OVERVIEW_COLUMN_VISIBILITY)
  );
  const [customColumnsConfig, setCustomColumnsConfig] = useState<CustomColumnPreference[]>([]);
  const normalizedCustomColumns = useMemo(
    () => normalizeCustomColumnsConfig(customColumnsConfig),
    [customColumnsConfig]
  );
  const canCustomizeCustomColumns = !!account?.isOwner || !!account?.isAdmin;
  const visibleCustomColumns = useMemo(
    () =>
      canCustomizeCustomColumns
        ? normalizedCustomColumns
        : normalizedCustomColumns.filter((column) => isConfiguredCustomColumn(column)),
    [canCustomizeCustomColumns, normalizedCustomColumns]
  );
  const hiddenColumnKeys = useMemo<ProjectOverviewColumnKey[]>(
    () =>
      canCustomizeCustomColumns
        ? []
        : normalizedCustomColumns
            .filter((column) => !isConfiguredCustomColumn(column))
            .map((column) => CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID[column.id as CustomColumnId]),
    [canCustomizeCustomColumns, normalizedCustomColumns]
  );
  const [isColumnVisibilityLoaded, setIsColumnVisibilityLoaded] = useState(false);
  const [currentDate, setCurrentDate] = useState<Date>(() => new Date());
  const [roleAssignmentColumns, setRoleAssignmentColumns] = useState<RoleAssignmentRole[]>([]);
  const [isRoleAssignmentConfigLoaded, setIsRoleAssignmentConfigLoaded] = useState(false);
  const [openRoleAssignmentDropdownKey, setOpenRoleAssignmentDropdownKey] = useState<string | null>(
    null
  );
  const assignmentOptionsByRoleIdRef = useRef<RoleAssignmentOptionsByRoleId>({});
  const [roleColumnVisibility, setRoleColumnVisibility] = useState<Record<number, boolean>>({});
  const [unsetRoleColumns, setUnsetRoleColumns] = useState<RoleAssignmentRole[]>([]);
  // ADD TEAM MEMBER WIDE SHEET
  const [accountIsOwner, setAccountIsOwner] = useState(false);
  const canManageTeamMembers = accountIsOwner || accountIsAdmin;
  const hasResolvedTeamSettingsAccess = props.hasResolvedTeamSettingsAccess !== false;
  const canUseRoleAssignments = props.canAccessTeamSettings === true;
  const [isInvitePanelOpen, setIsInvitePanelOpen] = useState(false);
  const [openPermissionTooltip, setOpenPermissionTooltip] = useState<PermissionTooltipKey | null>(
    null
  );
  const [invitePanelMode, setInvitePanelMode] = useState<InvitePanelMode>('team_member');
  const [usedSlots, setUsedSlots] = useState(0);
  const [availableSlots, setAvailableSlots] = useState(0);
  const normalizedAvailableSlots = Math.max(availableSlots, 0);
  const isSeatLimitReached = usedSlots >= normalizedAvailableSlots;
  const [isInvitingMember, setIsInvitingMember] = useState(false);
  const [memberEmail, setMemberEmail] = useState('');
  const [memberSelectedRoleIds, setMemberSelectedRoleIds] = useState<number[]>([]);
  const [memberPricingAccess, setMemberPricingAccess] = useState(false);
  const [memberAdminAccess, setMemberAdminAccess] = useState(false);
  const [assigneeName, setAssigneeName] = useState('');
  const [teamScopeAccountId, setTeamScopeAccountId] = useState(
    props.accountUser?.teamContext?.owner?.accountId || accountId
  );
  const [assigneeLabel, setAssigneeLabel] = useState('');
  const [assigneeSelectedRoleIds, setAssigneeSelectedRoleIds] = useState<number[]>([]);
  const [teamMembers, setTeamMembers] = useState<any[]>(
    props.accountUser?.teamContext?.teamMembers || []
  );
  const [openSeatsModal, setOpenSeatsModal] = useState(false);
  const [seatState, setSeatState] = useState(initialSeatState);
  const [availableSeats, setAvailableSeats] = useState<any>(null);
  const [seatLoading, setSeatLoading] = useState<boolean>(false);

  useEffect(() => {
    setTeamMembers(props.accountUser?.teamContext?.teamMembers || []);
  }, [props.accountUser?.teamContext?.teamMembers]);

  const loadColumnVisibilityPreference = useCallback(async () => {
    if (!accountId) {
      setIsColumnVisibilityLoaded(true);
      return;
    }

    setIsColumnVisibilityLoaded(false);
    try {
      const preference = await getColumnVisibilityPreference({
        accountId,
        page: COLUMN_VISIBILITY_PAGES.projects,
      });
      setColumnVisibility(
        resolveProjectsColumnVisibility(
          normalizeColumnVisibilityPreference(PROJECTS_COLUMN_KEYS, preference?.visibility?.columns)
        )
      );
      setRoleColumnVisibility(
        normalizeAssignmentColumnVisibility(preference?.visibility?.assignmentColumns)
      );
      setCustomColumnsConfig(
        Array.isArray(preference?.visibility?.customColumns)
          ? preference.visibility.customColumns
          : []
      );
    } catch (error) {
      console.error('Failed to load column visibility preferences for projects page:', error);
    } finally {
      setIsColumnVisibilityLoaded(true);
    }
  }, [accountId]);

  useEffect(() => {
    void loadColumnVisibilityPreference();
  }, [loadColumnVisibilityPreference]);

  const ownerTeamMemberAccountId = teamMembers.find((member) => member.isOwner)?.accountId || 0;
  const ownerAccountIdFromStorage = readOwnerAccountIdFromStorage();
  const managedAccountId = teamScopeAccountId || accountId;
  const roleAssignmentOwnerAccountId = useMemo(() => {
    const resolvedOwnerAccountId = resolveRoleAssignmentOwnerAccountId(
      accountId,
      props.accountUser?.teamContext || {}
    );

    return resolvedOwnerAccountId || teamScopeAccountId || managedAccountId || accountId;
  }, [
    accountId,
    managedAccountId,
    props.accountUser?.teamContext?.owner?.accountId,
    props.accountUser?.teamContext?.ownerAccountId,
    teamScopeAccountId,
  ]);
  const assigneeScopeAccountId =
    ownerTeamMemberAccountId || ownerAccountIdFromStorage || managedAccountId;
  const [assigneeMutationState, setAssigneeMutationState] = useState<
    null | 'adding' | 'editing' | 'deleting'
  >(null);
  const [assigneesWithoutLogin, setAssigneesWithoutLogin] = useState<AssigneeWithoutLoginRow[]>([]);
  // @ts-ignore
  const [teamMemberPermissionBaseline, setTeamMemberPermissionBaseline] = useState<
    Record<string, TeamMemberPermissionDraft>
  >({});
  // @ts-ignore
  const [pendingTeamMemberPermissions, setPendingTeamMemberPermissions] = useState<
    Record<string, TeamMemberPermissionDraft>
  >({});
  // @ts-ignore
  const [ownerEmail, setOwnerEmail] = useState('');
  // @ts-ignore
  const [pendingSlots, setPendingSlots] = useState(0);
  const [pendingInvitations, setPendingInvitations] = useState<PendingInvitationRow[]>([]);
  // @ts-ignore
  const [planName, setPlanName] = useState('');
  const assignmentOptionsByRoleId = useMemo<RoleAssignmentOptionsByRoleId>(() => {
    if (!canUseRoleAssignments) {
      return {};
    }

    return buildRoleAssignmentOptionsByRoleId(
      roleAssignmentColumns,
      extractTeamMembersForRoleAssignments({ teamMembers }),
      extractAssigneesForRoleAssignments(assigneesWithoutLogin)
    );
  }, [assigneesWithoutLogin, canUseRoleAssignments, roleAssignmentColumns, teamMembers]);
  const teamMembersRef = useRef<any[]>(teamMembers);
  const assigneesWithoutLoginRef = useRef<AssigneeWithoutLoginRow[]>(assigneesWithoutLogin);
  const assigneeScopeAccountIdRef = useRef<number>(assigneeScopeAccountId);
  assignmentOptionsByRoleIdRef.current = assignmentOptionsByRoleId;
  teamMembersRef.current = teamMembers;
  assigneesWithoutLoginRef.current = assigneesWithoutLogin;
  assigneeScopeAccountIdRef.current = assigneeScopeAccountId;
  const isAssigneeMutationLoading = assigneeMutationState !== null;
  const canSubmitInvitePanel =
    invitePanelMode === 'team_member'
      ? memberEmail.trim().length > 0 && !isSeatLimitReached && !isInvitingMember
      : assigneeName.trim().length > 0 &&
        assigneeLabel.trim().length > 0 &&
        assigneeSelectedRoleIds.length > 0 &&
        !isAssigneeMutationLoading;
  const [isAssigneeLabelAuto, setIsAssigneeLabelAuto] = useState(true);
  const [roles, setRoles] = useState<RolePermissionRow[]>([]);
  const [isRemovingInactiveMembers, setIsRemovingInactiveMembers] = useState(false);
  // @ts-ignore
  const [resendingInvitationId, setResendingInvitationId] = useState<string | null>(null);
  // @ts-ignore
  const [deletingInvitationId, setDeletingInvitationId] = useState<string | null>(null);
  const seatUsagePercent = normalizedAvailableSlots
    ? Math.min((Math.max(usedSlots, 0) / normalizedAvailableSlots) * 100, 100)
    : 0;
  const remainingSeatsAfterInvite = Math.max(normalizedAvailableSlots - (usedSlots + 1), 0);
  // @ts-ignore
  const [openTablePermissionTooltip, setOpenTablePermissionTooltip] =
    useState<PermissionTooltipKey | null>(null);
  // @ts-ignore
  const [tablePermissionTooltipPosition, setTablePermissionTooltipPosition] =
    useState<TablePermissionTooltipPosition | null>(null);
  // END OF ADD TEAM MEMBER WIDE SHEET
  // ####### END OF STATES #######
  // ####### FUNCTIONS #######

  const handleChangeValueSearchInput = (e: any) => {
    const { value } = e.target;
    setSearchTerm(value);

    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    debounceTimer.current = setTimeout(() => {
      handleRefetch();
    }, SEARCH_INPUT_DEBOUNCE_MS);
  };

  const handleSearchInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    setSearchSubmitTrigger((prev) => prev + 1);
    handleRefetch();
  };

  const handleClearSearchKeyword = () => {
    setSearchTerm('');
    handleRefetch();
  };

  useEffect(() => {
    async function getAcc() {
      const res = await getAccount(accountId);
      if (!res.error) {
        setRemainingItemOrder(res.remainingItemOrder || 0);
        setAccountCanSeePricing(!!res.canSeePricing);
        setAccountIsAdmin(!!res.isAdmin);
        setAccount(res);
        setAccountIsOwner(res?.isOwner || false);
      }
    }
    getAcc();
  }, []);

  const print = useCallback(async (pList: ClientDashboardModule.ClientProject[]) => {
    try {
      const storeId = `${accountId}_pdfs`;
      const workOrderPdfLinks = pList
        .filter((p) => p.hasOwnProperty('workOrderId'))
        .map((item) => item.workOrderPdfLink);
      const res = await mergePdfFiles(workOrderPdfLinks, storeId);
      const url = await getMergedPdfPrintUrl(res);
      const iframe = document.createElement('iframe');
      iframe.style.position = 'fixed';
      iframe.style.right = '0';
      iframe.style.bottom = '0';
      iframe.style.width = '0';
      iframe.style.height = '0';
      iframe.style.border = '0';
      iframe.src = url;
      document.body.appendChild(iframe);
      const handle = () => {
        try {
          if (iframe.contentWindow) {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
          }
        } finally {
          setIsPrintModalOpen(false);
        }
      };
      handle();
    } catch (error) {
      showErrorToast(
        isMergePdfNoneFoundError(error)
          ? 'None of the selected PDFs were found. Open the order and regenerate PDF, then try again.'
          : 'Unable to print selected work orders. Please try again.'
      );
      setIsPrintModalOpen(false);
    } finally {
      setGeneratePdf(false);
    }
  }, []);

  useEffect(() => {
    if (window.location.search && clients.length > 0) {
      const clientId = new URLSearchParams(window.location.search).get('clientId');
      const filter = clients.find((client: any) => client.id === Number(clientId));
      if (filter) {
        setFilters((prev: any) => {
          return {
            ...prev,
            client: {
              ...prev['client'],
              value: filter.displayName ? [filter.displayName] : [],
            },
          };
        });
        handleRefetch();
      }
    }
  }, [clients]);

  const handleSortTable = (col: any) => {
    setSort((prev: any) => {
      const pos_0 = prev.position === 0;
      const pos_2 = prev.position === 2;
      const diff_col = prev.column && prev.column !== col;
      return {
        ...prev,
        column: diff_col ? col : pos_2 ? 'lastViewedAt' : col,
        value: diff_col
          ? 'ASC'
          : pos_2 && col === 'lastViewedAt'
            ? 'ASC'
            : pos_2
              ? 'DESC'
              : pos_0
                ? 'ASC'
                : 'DESC',
        position: diff_col
          ? 1
          : pos_2 && col === 'lastViewedAt'
            ? 1
            : pos_2
              ? 0
              : prev.position + 1,
      };
    });
    setRefetch((prev) => !prev);
  };

  // TODO: Asked to be removed in task 2472
  // const handleSelectRows = (row: any) => {
  //   setSelectedRows((prev: any) => {
  //     return prev.includes(row) ? prev.filter((f: any) => f !== row) : [...prev, row];
  //   });
  // };

  // TODO: Asked to be removed in task 2472
  // const handleCheckAllRows = () => {
  //   const row_ids =
  //     selectedRows && selectedRows.length > 0
  //       ? []
  //       : tableData && tableData.length > 0
  //         ? tableData.map((t: any) => t.id)
  //         : [];
  //   setSelectedRows(row_ids);
  // };

  const handleRefetch = () => {
    setRefetch((prev) => !prev);
  };

  const handleRefetchRoles = () => {
    setRefetchRoles((prev) => !prev);
  };

  const handleRefetchProjectStatuses = () => {
    setRefetchProjectStatuses((prev) => !prev);
  };

  const handleResetAfterAction = () => {
    setOpenConfirmationModal({
      active: false,
      action: '',
    });
    handleRefetch();
    setSelectedRows([]);
  };

  const handleResetAllFilters = () => {
    setFilters({
      client: { value: [], archived: false },
      status: { value: [] },
      estimate: { value: [] },
      invoice: { value: [] },
      assignee: { value: [] },
      lastViewedAt: { value: [] },
    });
    handleRefetch();
  };

  const handleChooseFilter = (filter: string, filter_field: any) => {
    switch (filter_field) {
      case 'status':
      case 'invoice':
      case 'estimate':
      case 'assignee':
        let set_array = filters[filter_field].value || [];

        if (
          filters[filter_field] &&
          filters[filter_field].value &&
          filters[filter_field].value.includes(filter)
        ) {
          set_array = set_array.filter((s: any) => s !== filter);
        } else {
          set_array = [...set_array, filter];
        }

        setFilters((prev: any) => {
          return {
            ...prev,
            [filter_field]: {
              ...prev[filter_field],
              value: set_array,
            },
          };
        });
        break;
      case 'client':
      case 'lastViewedAt':
        setFilters((prev: any) => {
          return {
            ...prev,
            [filter_field]: {
              ...prev[filter_field],
              value: filter ? [filter] : [],
            },
          };
        });
        break;
      default:
        return;
    }

    if (!isMobile) {
      handleRefetch();
    }
  };

  const handleClearAllFilter = (filter_field: any) => {
    switch (filter_field) {
      case 'status':
      case 'invoice':
      case 'estimate':
      case 'assignee':
        setFilters((prev: any) => {
          return {
            ...prev,
            [filter_field]: {
              ...prev[filter_field],
              value: [],
            },
          };
        });
        break;
      default:
        return;
    }

    if (!isMobile) {
      handleRefetch();
    }
  };

  const handleShowArchived = (filter_name: string) => {
    setFilters((prev: any) => {
      return {
        ...prev,
        [filter_name]: {
          ...prev[filter_name],
          archived: !prev[filter_name].archived,
        },
      };
    });
  };

  const handleNewClientProject = async (type: string) => {
    setIsSubmenuOpened(false);
    const can_create_order = await userCanCreateNewOrder(accountId);
    if (!can_create_order && remainingItemOrder === 0) {
      props.handleOpenSubscriptionPlanModal();
      return;
    }
    props.history.push(`/calculator?type=${type}`, { type: type, referrer: location.pathname });
  };

  const handleOpenAssistant = () => {
    setIsSubmenuOpened(false);
    openQuipaAssistant({ source: 'projects' });
  };

  const handleClickOutside = (event: MouseEvent): void => {
    if (submenuRef && submenuRef.current && !submenuRef.current.contains(event.target as Node)) {
      setIsSubmenuOpened(false);
    }
  };

  const handleRecursiveParent = (client: any, name_str: any = []): any => {
    if (client && client.parentClient) {
      name_str.push(client.parentClient);
      return handleRecursiveParent(client.parentClient, name_str);
    }
    return name_str;
  };

  const handleStatusColor = (key: any, val: any) => {
    let class_name = 'base_status_color';

    const default_statuses =
      initial_project_statuses && initial_project_statuses.length > 0
        ? initial_project_statuses.map((s: any) => s.value)
        : [];

    if (val) {
      if (key === 'status' && !default_statuses.includes(val)) {
        class_name = 'custom_status_color';
      } else {
        class_name = key + '_' + val.split(' ').join('_').toLowerCase();
      }
    }

    return class_name;
  };

  const handleUpdateProject = useCallback(async (obj: any, key: any, value: any) => {
    const keys = key.split('.');
    const lastKey = keys.slice(-1)[0];
    let current = obj;

    if (keys && keys.length === 1) {
      current[lastKey] = value;
    } else if (keys && keys.length > 1) {
      const objectHelper = (obj1: any, keys: any, new_value: any): any => {
        const temp_key = keys[0];
        if (keys.length === 1) {
          if (obj1 && typeof obj1 === 'object' && temp_key in obj1) {
            return {
              ...obj1,
              [temp_key]: new_value,
              ...(temp_key === 'notes' && { notesUpdatedAt: new Date() }),
            };
          }
          return obj1;
        }

        if (
          !(
            obj1 &&
            typeof obj1 === 'object' &&
            temp_key in obj1 &&
            typeof obj1[temp_key] === 'object' &&
            obj1[temp_key] !== null
          )
        ) {
          return obj1;
        }

        let return_obj = {
          ...obj1,
          [temp_key]: objectHelper(obj1[temp_key], keys.slice(1), new_value),
        };

        return return_obj;
      };

      const temp_obj = objectHelper(obj, keys, value);
      current = temp_obj;
    }

    setTableData((prev) => {
      return prev.map((i: any) => {
        if (i.id === current.id) {
          return {
            ...i,
            ...current,
          };
        }

        return i;
      });
    });
    await updateProject(obj.id, current);

    if (key === 'status') {
      trackView(MixpanelEvents.CHANGE_PROJECT_STATUS);
    }
  }, []);

  const updateNotebook = (projId: number, filterOut: string) => {
    if (!projId) return;
    let foundProj = tableData?.find((i) => i.id === projId);
    if (!foundProj) return;
    let notebook = foundProj?.notebook || [];
    let filteredNotebook = notebook?.filter((i: any) => i.type !== filterOut);
    foundProj.notebook = filteredNotebook || [];

    setTableData((prev) => {
      return prev?.map((item: any) => {
        if (item?.id === foundProj?.id) {
          return foundProj;
        }
        return item;
      });
    });
  };

  const handleOpenConfirmModal = (action: any, id?: any) => {
    if (!accountCanSeePricing) {
      return;
    }
    // TODO: Asked to be removed in task 2472
    // if (selectedRows && !selectedRows.includes(id)) {
    //   setSelectedRows((prev: any) => [...prev, id]);
    // }
    setSelectedRows([id]);
    setOpenConfirmationModal({
      active: true,
      action: action,
    });
  };

  const handleDeleteProject = async () => {
    const payload = {
      ids: selectedRows,
    };
    const res = await deleteProject(payload);
    if (res) {
      handleResetAfterAction();
    }
  };

  const handleArchiveProjects = async () => {
    const payload = {
      ids: selectedRows,
    };
    const res = await archiveProjects(payload);
    if (res) {
      handleResetAfterAction();
    }
  };

  const handleConfirmModal = () => {
    switch (openConfirmationModal.action) {
      case 'delete':
        handleDeleteProject();
        break;
      case 'archive':
        handleArchiveProjects();
        break;
      default:
        return;
    }
  };

  const handleChangeDate = (obj: any, field: string, v: Date | undefined) => {
    if (!field) return;

    const newValue = !v ? null : v;
    handleUpdateProject(obj, field, newValue);
  };

  const handleGoToClient = async (client: any) => {
    if (client && client.id) {
      // Track lastViewedAt for project
      await handleUpdateLastViewedProject(client?.id);
      props.history.push(`/clients/${client.id}?project=true`);
      // location.href = `clients/${client.id}?project=true`;
    }
  };

  const highlightText = (text: any, searchTerm: any, delay?: number) => {
    if (!searchTerm) return text;
    if (!searchTerm.trim()) return text;

    const escapedSearchTerm = searchTerm.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');

    const regex = new RegExp(`(${escapedSearchTerm})`, 'gi');
    const parts = text.split(regex);

    return parts.map((part: any, index: any) =>
      regex.test(part) ? (
        <span key={index} className="searchTerm">
          {part}
        </span>
      ) : (
        part
      )
    );
  };

  const handleColumnVisibilityChange = (next: ProjectOverviewColumnVisibility) => {
    const resolvedNext = resolveProjectsColumnVisibility(next);
    setColumnVisibility(resolvedNext);
    if (!accountId) {
      return;
    }
    void upsertColumnVisibilityPreference({
      accountId,
      page: COLUMN_VISIBILITY_PAGES.projects,
      visibility: { columns: resolvedNext },
    }).catch((error) => {
      console.error('Failed to save column visibility preferences for projects page:', error);
    });
  };

  const handleSaveCustomColumns = async (customColumns: CustomColumnPreference[]) => {
    if (!accountId) {
      return;
    }

    const response = await saveCustomColumnsPreference({
      accountId,
      page: COLUMN_VISIBILITY_PAGES.projects,
      customColumns,
    });

    setCustomColumnsConfig(
      Array.isArray(response?.visibility?.customColumns)
        ? response.visibility.customColumns
        : customColumns
    );
  };

  const handleRoleColumnVisibilityChange = (next: Record<number, boolean>) => {
    setRoleColumnVisibility(next);
    void upsertColumnVisibilityPreference({
      accountId,
      page: COLUMN_VISIBILITY_PAGES.projects,
      visibility: { assignmentColumns: next },
    }).catch((error) => {
      console.error('Failed to save assignment column visibility for projects page:', error);
    });
  };

  const handleCloseInvitePanel = () => {
    setIsInvitePanelOpen(false);
    setOpenPermissionTooltip(null);
  };

  const ensureCanManageTeamMembers = (): boolean => {
    if (canManageTeamMembers) {
      return true;
    }
    showErrorToast('You do not have permission to edit team members.');
    return false;
  };

  const getUsedAssigneeLabels = (excludeAssigneeId?: number): Set<string> =>
    collectUsedLabels(teamMembers, pendingInvitations, assigneesWithoutLogin, excludeAssigneeId);

  const fetchAssignees = async (scopeAccountId: number = assigneeScopeAccountId) => {
    if (!scopeAccountId) {
      setAssigneesWithoutLogin([]);
      return;
    }

    try {
      const assignees = await getAllAssigneesByAccountId({ accountId: scopeAccountId });
      setAssigneesWithoutLogin((assignees || []).map(mapAssigneePayloadToRow));
    } catch (error) {
      showErrorToast('Failed to fetch assignees. Please try again later.');
    }
  };

  const loadSeatUsage = async () => {
    if (!managedAccountId) {
      return;
    }

    try {
      const planInfoRes = await getCustomerPlanInfo(managedAccountId);
      let planInfo = planInfoRes['main'];
      let planInfoSeat = planInfoRes['seat'];

      if (!planInfoRes || planInfoRes.error || !planInfo) {
        return;
      }
      if (typeof planInfo.planName === 'string') {
        setPlanName(planInfo.planName);
      }
      let mainPlanInfo = extractSeatLimitFromPlanInfo(planInfo);
      let seatsSum = (planInfoSeat?.quantity || 0) + mainPlanInfo;
      setAvailableSlots(seatsSum);
    } catch (error) {
      // Seat usage is supplemental UI data; keep page functional if unavailable.
      console.error('Failed to load seat usage:', error);
    }
  };

  const fetchTeamData = async () => {
    if (!accountId) {
      return;
    }

    try {
      const user = await getUser({ accountId, includeTeamData: true });
      const ownerAccountId = resolveOwnerAccountIdFromTeamContext(
        user?.teamContext,
        ownerAccountIdFromStorage || managedAccountId || accountId
      );

      if (ownerAccountId && ownerAccountId !== managedAccountId) {
        setTeamScopeAccountId(ownerAccountId);
      }

      const allTeamMembers = Array.isArray(user?.teamContext?.teamMembers)
        ? user.teamContext.teamMembers
        : [];
      const members = allTeamMembers.filter(
        (member: any) => String(member?.type || '').toLowerCase() === 'subscribed'
      );

      const mappedMembers: TeamMemberRow[] = members.map((member: any) => ({
        id: !!member?.isOwner ? 'owner' : `account-${Number(member?.accountId || 0)}`,
        accountId: Number(member?.accountId || 0),
        firstName: String(member?.firstName || '').trim(),
        lastName: String(member?.lastName || '').trim(),
        email: String(member?.email || '').trim(),
        label: normalizeAssigneeLabelInput(String(member?.label || '').toUpperCase()),
        roles: normalizeRoleIds(member?.roles),
        isOwner: !!member?.isOwner,
        isAdmin: !!member?.isAdmin,
        pricingEnabled: !!member?.canSeePricing,
      }));

      let resolvedTeamMembers: TeamMemberRow[] = mappedMembers;
      if (mappedMembers.length === 0) {
        const fallbackIsOwner =
          !!user?.isOwner || (ownerAccountId > 0 && ownerAccountId === accountId);
        const fallbackIsAdmin = !!user?.isAdmin;
        resolvedTeamMembers = [
          {
            id: 'owner',
            accountId: ownerAccountId || accountId,
            firstName,
            lastName,
            email: String(user?.email || '').trim(),
            label: normalizeAssigneeLabelInput(String(user?.label || '').toUpperCase()),
            roles: normalizeRoleIds(user?.roles),
            isOwner: fallbackIsOwner,
            isAdmin: fallbackIsAdmin,
            pricingEnabled: !!user?.canSeePricing,
          },
        ];
      }
      // setTeamMembers(resolvedTeamMembers);
      setTeamMemberPermissionBaseline(
        resolvedTeamMembers.reduce<Record<string, TeamMemberPermissionDraft>>((acc, member) => {
          acc[member.id] = {
            accountId: member.accountId,
            pricingEnabled: member.pricingEnabled,
            isAdmin: member.isAdmin,
          };
          return acc;
        }, {})
      );
      setPendingTeamMemberPermissions({});

      const ownerMember = mappedMembers.find((member) => member.isOwner);
      const ownerMemberSource = allTeamMembers.find(
        (member: any) => Number(member?.accountId) === ownerMember?.accountId
      );
      const ownerEmailFromContext =
        typeof user?.teamContext?.owner?.email === 'string' ? user.teamContext.owner.email : '';
      const currentUserEmail = typeof user?.email === 'string' ? user.email : '';
      setOwnerEmail(
        String(ownerMemberSource?.email || ownerEmailFromContext || currentUserEmail || '').trim()
      );

      const invitations = Array.isArray(user?.teamContext?.pendingInvitations)
        ? user.teamContext.pendingInvitations
        : [];
      const pendingInvitationsCount = invitations.length;
      setPendingSlots(pendingInvitationsCount);
      const ownerExistsInTeam = mappedMembers.some((member) => member.isOwner);
      const activeTeamMembersCount =
        mappedMembers.length > 0 ? mappedMembers.length + (ownerExistsInTeam ? 0 : 1) : 1;
      setUsedSlots(activeTeamMembersCount + pendingInvitationsCount);
      setPendingInvitations(
        invitations.map((invitation: any) => ({
          id: `pending-account-${Number(invitation?.accountId || 0)}`,
          accountId: Number(invitation?.accountId || 0),
          email: String(invitation?.email || '').trim(),
          label: normalizeAssigneeLabelInput(String(invitation?.label || '').toUpperCase()),
          roleIds: normalizeRoleIds(invitation?.roles),
          sentLabel: formatTimeAgo(invitation?.createdAt),
          statusLabel: 'Pending',
          isAdmin: !!invitation?.isAdmin,
          canSeePricing: !!invitation?.canSeePricing,
        }))
      );
    } catch (error) {
      console.error('Failed to load team settings data:', error);
    }
  };

  const handleSubmitInvitePanel = async () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (invitePanelMode === 'team_member') {
      if (isSeatLimitReached) {
        showErrorToast('Seat limit reached. Add seats before inviting more team members.');
        return;
      }
      if (isInvitingMember) {
        return;
      }
      const trimmedEmail = memberEmail.trim();
      if (!trimmedEmail || !trimmedEmail.includes('@')) {
        showErrorToast('Please enter a valid email.');
        return;
      }
      if (!accountId) {
        showErrorToast('Account not found.');
        return;
      }

      try {
        setIsInvitingMember(true);
        await inviteMember({
          accountId,
          email: trimmedEmail,
          roles: memberSelectedRoleIds,
          canSeePricing: memberPricingAccess,
          isAdmin: memberAdminAccess,
        });
        await Promise.all([fetchTeamData(), loadSeatUsage()]);
        showSuccessToast(`Invitation sent - An invitation has been sent to ${trimmedEmail}`);
        handleCloseInvitePanel();
      } catch (error: any) {
        if (error?.response?.status === 409) {
          showErrorToast('A user with this email address is already a team member.');
        } else if (error?.response?.status === 403) {
          showErrorToast('You do not have permission to invite team members.');
        } else {
          showErrorToast('Failed to send invitation. Please try again later.');
        }
      } finally {
        setIsInvitingMember(false);
      }
      return;
    }

    const trimmedName = assigneeName.trim();
    const trimmedLabel = normalizeAssigneeLabelInput(assigneeLabel);
    if (!trimmedName) {
      showErrorToast('Assignee name is required.');
      return;
    }
    if (!trimmedLabel || trimmedLabel.length < 1 || trimmedLabel.length > 4) {
      showErrorToast('Label must be 1 to 4 characters.');
      return;
    }
    if (getUsedAssigneeLabels().has(trimmedLabel)) {
      showErrorToast('Label is already in use for this account');
      return;
    }
    if (assigneeSelectedRoleIds.length === 0) {
      showErrorToast('Select at least one role for the assignee.');
      return;
    }

    if (!assigneeScopeAccountId) {
      showErrorToast('Account not found.');
      return;
    }

    try {
      setAssigneeMutationState('adding');
      await addNewAssignee({
        accountId: assigneeScopeAccountId,
        name: trimmedName,
        label: trimmedLabel,
        roles: assigneeSelectedRoleIds,
      });
      await fetchAssignees();
      showSuccessToast(`Assignee added - ${trimmedName} has been added as assignee`);
      handleCloseInvitePanel();
    } catch (error: any) {
      const apiMessage = extractApiErrorMessage(error);
      showErrorToast(apiMessage || 'Failed to add assignee. Please try again later.');
    } finally {
      setAssigneeMutationState(null);
    }
  };

  const resetInvitePanelForm = (mode: InvitePanelMode) => {
    if (mode === 'team_member') {
      setMemberEmail('');
      // setMemberSelectedRoleIds([]);
      setMemberPricingAccess(false);
      setMemberAdminAccess(false);
      return;
    }
    setAssigneeName('');
    setAssigneeLabel('');
    setIsAssigneeLabelAuto(true);
    // setAssigneeSelectedRoleIds([]);
  };

  const handleSwitchInviteMode = (mode: InvitePanelMode) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setInvitePanelMode(mode);
    resetInvitePanelForm(mode);
    setOpenPermissionTooltip(null);

    if (mode === 'assignee') {
      void Promise.all([fetchTeamData(), fetchAssignees()]);
    }
  };

  const toggleMemberRole = (roleId: number, checked: boolean) => {
    setMemberSelectedRoleIds((prev) => {
      if (checked) {
        if (prev.includes(roleId)) {
          return prev;
        }
        return [...prev, roleId];
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  const handleChangePlan = async () => {
    try {
      const variant = checkGate(PRICING_TABLE_PLANS_GATE) ? 'new' : 'old';
      const session = await changeSubscriptionPlan(managedAccountId, variant);

      if (session?.url) {
        window.location.href = session.url;
        return;
      }

      const errorMessage = extractApiErrorMessage(session?.error) || session?.message;
      showErrorToast(errorMessage || 'Unable to open change plan page.');
    } catch (error) {
      const errorMessage = extractApiErrorMessage(error);
      showErrorToast(errorMessage || 'Unable to open change plan page.');
    }
  };

  const handleRemoveInactiveMembers = async () => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    if (isRemovingInactiveMembers || resendingInvitationId || deletingInvitationId) {
      return;
    }
    if (pendingInvitations.length === 0) {
      showToast('There is no inactive members');
      return;
    }

    try {
      setIsRemovingInactiveMembers(true);
      const results = await Promise.allSettled(
        pendingInvitations.map((invitation) =>
          updateAccount({
            accountId: invitation.accountId,
            type: 'unsubscribed',
          })
        )
      );

      const failedCount = results.filter((result) => result.status === 'rejected').length;
      const successCount = results.length - failedCount;

      await Promise.all([fetchTeamData(), loadSeatUsage()]);

      if (failedCount > 0) {
        showErrorToast('Failed to remove some inactive members. Please try again later.');
        return;
      }

      const message =
        successCount === 1
          ? '1 inactive member removed.'
          : `${successCount} inactive members removed.`;
      showSuccessToast(message);
    } catch (error) {
      showErrorToast('Failed to remove inactive members. Please try again later.');
    } finally {
      setIsRemovingInactiveMembers(false);
    }
  };

  const isInvitationActionDisabled = (): boolean => {
    return !!deletingInvitationId || !!resendingInvitationId || isRemovingInactiveMembers;
  };

  const handleAssigneeLabelChange = (value: string) => {
    const normalizedLabel = normalizeAssigneeLabelInput(value);
    setAssigneeLabel(normalizedLabel);
    setIsAssigneeLabelAuto(false);
  };

  const toggleAssigneeRole = (roleId: number, checked: boolean) => {
    setAssigneeSelectedRoleIds((prev) => {
      if (checked) {
        if (prev.includes(roleId)) {
          return prev;
        }
        return [...prev, roleId];
      }
      return prev.filter((id) => id !== roleId);
    });
  };

  const getUniqueAssigneeLabel = (name: string): string =>
    generateUniqueAssigneeLabel(name, getUsedAssigneeLabels());

  const handleAssigneeNameChange = (value: string) => {
    setAssigneeName(value);
    if (isAssigneeLabelAuto) {
      setAssigneeLabel(getUniqueAssigneeLabel(value));
    }
  };

  const handleOpenInvitePanel = (mode: InvitePanelMode, role?: any) => {
    if (!ensureCanManageTeamMembers()) {
      return;
    }
    setInvitePanelMode(mode);
    resetInvitePanelForm(mode);
    setOpenTablePermissionTooltip(null);
    setTablePermissionTooltipPosition(null);
    setIsInvitePanelOpen(true);
    setMemberSelectedRoleIds([role?.id]);
    setAssigneeSelectedRoleIds([role?.id]);

    if (mode === 'assignee') {
      void Promise.all([fetchTeamData(), fetchAssignees()]);
    }
  };

  const handleUpdateTeamMember = useCallback(
    async (val: any, member: any, role: any) => {
      if (!member) return;
      if (!role) return;

      let memberRoles: any[] = [];
      if (val) {
        memberRoles = [...new Set([...(member?.roles || []), role?.id])];
      } else if (member?.roles?.length > 0) {
        memberRoles = member?.roles?.filter((f: any) => f !== role?.id);
      }

      let payload = {
        accountId: member?.accountId,
        roles: memberRoles,
      };

      let newTeamMembers = (teamMembersRef.current || []).map((i) => {
        if (i?.userId === member?.userId) {
          return {
            ...i,
            roles: memberRoles,
          };
        }
        return i;
      });

      setTeamMembers(newTeamMembers);

      if (props.handleUpdateTeamMembers) {
        props.handleUpdateTeamMembers(newTeamMembers);
      }

      const res = await updateAccount(payload);
      if (!res) {
        console.error('Error while updating Account');
      }
    },
    [props.handleUpdateTeamMembers]
  );

  const handleUpdateAssignee = useCallback(async (val: any, assignee: any, role: any) => {
    if (!assignee) return;
    if (!role) return;

    const scopeAccountId = assigneeScopeAccountIdRef.current;
    if (!scopeAccountId) {
      showErrorToast('Account not found.');
      return;
    }

    const previousRoleIds = Array.isArray(assignee?.roles) ? [...assignee.roles] : [];
    const nextRoleIds = val
      ? [...new Set([...(assignee?.roles || []), role?.id])]
      : (assignee?.roles || []).filter((roleId: any) => roleId !== role?.id);

    setAssigneesWithoutLogin((currentAssignees) =>
      currentAssignees.map((currentAssignee) =>
        currentAssignee.id === assignee.id
          ? { ...currentAssignee, roles: [...nextRoleIds] }
          : currentAssignee
      )
    );

    try {
      await editAssignee({
        accountId: scopeAccountId,
        id: assignee.id,
        roles: [...nextRoleIds],
      });
    } catch (error) {
      setAssigneesWithoutLogin((currentAssignees) =>
        currentAssignees.map((currentAssignee) =>
          currentAssignee.id === assignee.id
            ? { ...currentAssignee, roles: previousRoleIds }
            : currentAssignee
        )
      );
      showErrorToast('Failed to update assignee roles. Please try again later.');
    }
  }, []);

  const handleChangeSeatQty = (e: ChangeEvent<HTMLInputElement>) => {
    let { value } = e.target;

    let numberRegex = /^[1-9]\d*$/;
    if (value && !numberRegex.test(value)) return;

    setSeatState((prev) => {
      return {
        ...prev,
        quantity: value,
      };
    });
  };

  const handleOpenSeatsModal = async (val: boolean) => {
    setOpenSeatsModal(val);
    setIsInvitePanelOpen(false);
    if (val) {
      setSeatLoading(true);
      const res = await getSeatInfo(accountId);
      if (res) {
        let defaultIntervalPrice: any = res?.prices?.data?.find(
          (i: any) => i?.recurring?.interval === seatState.interval
        );
        let unit_amount = defaultIntervalPrice?.unit_amount || 0;
        let defaultIntervalCost = unit_amount / 100 || 0;

        setAvailableSeats(res);
        setSeatState((prev) => {
          return {
            ...prev,
            cost: defaultIntervalCost?.toFixed(2),
          };
        });
        setSeatLoading(false);
      }
    } else {
      setAvailableSeats(null);
      setSeatState(initialSeatState);
      setSeatLoading(false);
    }
  };

  const handleChangeInterval = (val: string) => {
    setSeatState((prev) => ({ ...prev, interval: val }));
    let foundInterval = availableSeats?.prices?.data?.find(
      (i: any) => i?.recurring?.interval === val
    );
    let unit_amount = foundInterval?.unit_amount || 0;
    let newCost = unit_amount / 100 || 0;
    setSeatState((prev) => {
      return {
        ...prev,
        cost: newCost?.toFixed(2),
      };
    });
  };

  const handleBuyMoreSeats = async () => {
    setSeatLoading(true);
    let priceObj = availableSeats?.prices?.data?.find(
      (i: any) => i?.recurring?.interval === seatState.interval
    );
    let payload = {
      accountId: accountId,
      quantity: Number(seatState.quantity) || 1,
      seat: priceObj,
    };
    const res = await buyAdditionalSeats(payload);
    if (res) {
      setOpenSeatsModal(false);
      setSeatState(initialSeatState);
      await Promise.all([fetchTeamData(), loadSeatUsage()]);
      setSeatLoading(false);
    }
  };
  // ####### END OF FUNCTIONS #######

  // ####### FETCH FUNCTIONS #######
  const fetchRoles = async () => {
    if (!managedAccountId) {
      setRoles([]);
      return;
    }

    try {
      const roles = await getAllRolesByAccountId({ accountId: managedAccountId });

      setRoles(roles);
    } catch (error) {
      showErrorToast('Failed to fetch roles. Please try again later.');
    }
  };

  const loadClients = async (): Promise<void> => {
    let res: any;
    if (accountId) {
      res = await getClientOptions({
        accountId,
        sort: { column: 'displayName', value: 'ASC' },
        includeInactive: true,
      });
    }

    if (res && !res.error) {
      const convertToSortTableRowInfo = (client: any) => {
        const rowInfo: any = client;
        if (client.subClients) {
          rowInfo.subList = client.subClients.map(convertToSortTableRowInfo);
        }
        return rowInfo;
      };

      const clients = res.map(convertToSortTableRowInfo);
      const allSubClients = clients.reduce(
        (acc: any, client: any) => acc.concat(client.subList || []),
        [] as any[]
      );

      const updatedClients = [...clients, ...allSubClients];

      setClients(updatedClients);
    }
  };

  const fetchProjects = async () => {
    const { position, ...rest } = sort;
    const filtersPayload = {
      ...filters,
      assignee: canUseRoleAssignments ? filters.assignee.value : [],
    };
    const payload = {
      accountId: accountId,
      sort: rest,
      filters: filtersPayload,
      search: searchTerm,
    };
    setLoading(true);
    const res = await getProjects(payload);
    if (res && !res.error) {
      setTableData(res);
    }
    setLoading(false);
    if (searchInputRef && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  };

  const loadEstimatesForClient = useCallback(
    async (clientId: number): Promise<ProjectEstimate[]> => {
      const normalizedClientId = Number(clientId || 0);
      if (!normalizedClientId || !accountId) return [];
      const response = await getAllEstimates({
        clientId: normalizedClientId,
        accountId,
      });
      return Array.isArray(response) ? (response as ProjectEstimate[]) : [];
    },
    [accountId]
  );

  const loadQuickBooksConnectionState = useCallback(async () => {
    if (!accountId) return false;
    try {
      const response = await quickbooksCheckIsConnected(accountId);
      return response?.connected === true && !!response?.userinfo;
    } catch (error) {
      console.error('Failed to check QuickBooks connection:', error);
      return false;
    }
  }, [accountId]);

  const handleOpenEstimateFromStatus = useCallback(
    async (estimateId: number, client?: any) => {
      if (!accountCanSeePricing) return;
      const normalizedEstimateId = Number(estimateId || 0);
      const normalizedClientId = Number(client?.id || 0);
      if (
        !Number.isInteger(normalizedEstimateId) ||
        normalizedEstimateId <= 0 ||
        !normalizedClientId
      ) {
        return;
      }

      try {
        const [estimates, qboConnected] = await Promise.all([
          loadEstimatesForClient(normalizedClientId),
          loadQuickBooksConnectionState(),
        ]);
        setEstimateSheetState({
          open: true,
          estimateId: normalizedEstimateId,
          client,
          estimates,
          qboConnected,
        });
      } catch (error) {
        console.error('Failed to load estimate from status link:', error);
        setEstimateSheetState({
          open: true,
          estimateId: normalizedEstimateId,
          client,
          estimates: [],
          qboConnected: false,
        });
      }
    },
    [accountCanSeePricing, loadEstimatesForClient, loadQuickBooksConnectionState]
  );

  const handleEstimateSheetChanged = async () => {
    await fetchProjects();
    const activeClientId = Number(estimateSheetState.client?.id || 0);
    if (!activeClientId) return;

    try {
      const estimates = await loadEstimatesForClient(activeClientId);
      setEstimateSheetState((prev) => ({ ...prev, estimates }));
    } catch (error) {
      console.error('Failed to refresh estimates after estimate change:', error);
    }
  };

  const handleCloseEstimateSheet = () => {
    setEstimateSheetState((prev) => ({
      ...prev,
      open: false,
      estimateId: null,
    }));
  };

  const handleFetchProjectStatuses = async () => {
    if (!accountId) {
      console.error('Account ID is missing!');
      return;
    }
    const payload = {
      accountId: accountId,
    };
    const response = await getProjectStatuses(payload);
    if (response && !response.error) {
      const mapped_res =
        response && response.length > 0
          ? response.map((i: any) => {
              return {
                ...i,
                value: i.name,
              };
            })
          : [];
      const merged_statuses: any[] = [...mapped_res, ...initial_project_statuses];
      setProjectStatuses(merged_statuses);
    }
  };

  const fetchRoleAssignmentConfig = useCallback(async () => {
    setIsRoleAssignmentConfigLoaded(false);
    if (!canUseRoleAssignments) {
      setRoleAssignmentColumns([]);
      setUnsetRoleColumns([]);
      setIsRoleAssignmentConfigLoaded(true);
      return;
    }

    if (!accountId) {
      setRoleAssignmentColumns([]);
      setUnsetRoleColumns([]);
      setIsRoleAssignmentConfigLoaded(true);
      return;
    }

    try {
      const [rolesResponse, assigneesResponse] = await Promise.all([
        getAllRolesByAccountId({ accountId: roleAssignmentOwnerAccountId }),
        getAllAssigneesByAccountId({ accountId: roleAssignmentOwnerAccountId }),
      ]);

      const availableRoleColumns = getRoleColumnsForTarget(rolesResponse, 'projects');
      const nextUnsetRoleColumns: any[] =
        rolesResponse?.filter((i: any) => i && i.projects === false) || [];

      setRoleAssignmentColumns((currentColumns) =>
        areRoleAssignmentColumnsEqual(currentColumns, availableRoleColumns)
          ? currentColumns
          : availableRoleColumns
      );
      setAssigneesWithoutLogin((assigneesResponse || []).map(mapAssigneePayloadToRow));
      setUnsetRoleColumns((currentColumns) =>
        areRoleAssignmentColumnsEqual(currentColumns, nextUnsetRoleColumns)
          ? currentColumns
          : nextUnsetRoleColumns
      );
    } catch (error) {
      console.error('Failed to load role assignments config for projects page:', error);
      setRoleAssignmentColumns([]);
      setUnsetRoleColumns([]);
    } finally {
      setIsRoleAssignmentConfigLoaded(true);
    }
  }, [accountId, canUseRoleAssignments, refetchRoles, roleAssignmentOwnerAccountId]);

  useEffect(() => {
    setRoleColumnVisibility((prev) => {
      if (roleAssignmentColumns.length === 0) {
        return {};
      }
      const next: Record<number, boolean> = {};
      roleAssignmentColumns.forEach((role) => {
        next[role.id] = prev[role.id] !== false;
      });
      return next;
    });
  }, [roleAssignmentColumns]);

  // const loadClientProjects = async () => {

  //   this.setState({
  //     client,
  //     initialClientState: client.active,
  //     projectList,
  //     // roomList,
  //     parentClients: client.parentClients,
  //     items: getProjectsRes.payload.itemNames,
  //     rooms: getProjectsRes.payload.rooms,
  //   });
  // };

  const fetchClientProjects = async (client: any) => {
    if (!accountCanSeePricing) {
      return;
    }
    const { accountId, id } = client;
    const sort: { field: string; direction: 'ASC' | 'DESC' } = { field: '', direction: 'ASC' };
    const showArchived = false;

    const filters_obj = {
      rooms: { value: [] },
      items: { value: [] },
      status: { value: [] },
    };
    const getProjectsRes = await getClientProjects({
      accountId,
      clientId: id,
      includeArchived: showArchived,
      sort,
      filters: filters_obj,
    });

    if (getProjectsRes.error) {
      return;
    }
    const res: ClientDashboardModule.ClientProject[] = getProjectsRes.payload.result;
    setProjectList(res);
    if (!res.every((p) => p.hasOwnProperty('workOrderId'))) {
      setIsPrintModalOpen(true);
      return;
    }
    handlePrint(client, res);
  };

  const handlePrint = async (client: any, pList?: ClientDashboardModule.ClientProject[]) => {
    setGeneratePdf(true);
    let arrProjects = [...projectList];
    if (pList) {
      arrProjects = [...pList];
    }
    const projects = arrProjects.filter(
      (p: ClientDashboardModule.ClientProject) => !p.workOrderPdfLink && p.workOrderId
    );
    if (projects.length > 0) {
      for (const project of projects) {
        if (project.id) {
          const res = await getWorkOrderPDFContent(project.id);
          const resDraperyOrder = await getAllInfo(project.id);
          const workOrder: WorkOrderModule.WorkOrder = resDraperyOrder.workOrder;
          const storeId = `${accountId}_pdfs`;
          let pdfLink;
          let dueDate = res.dueDate;
          let dueDateForLambda = dueDate ? moment(dueDate).local().format('MM/DD/YYYY') : '';
          let buffer;
          const needNewPDF = !moment
            .utc(workOrder.dueDatePdf || undefined)
            .startOf('day')
            .isSame(moment.utc(res.dueDate || undefined).startOf('day'));
          const responseForLambda = { ...res, dueDate: dueDateForLambda, needNewPDF: needNewPDF };

          console.log(workOrder.dueDatePdf, 'workOrder.dueDatePdf');
          console.log(res.dueDate, 'res.dueDate');
          console.log(dueDateForLambda, 'dueDateForLambda');
          console.log(workOrder.dueDatePdf, 'workOrder.dueDatePdf');
          console.log(workOrder, 'workOrder');
          console.log(res, 'res');

          console.log(needNewPDF, 'needNewPDF');
          console.log(responseForLambda, responseForLambda);

          if (!workOrder.pdfLink || needNewPDF) {
            const lambdaResponse = await callLambda(responseForLambda, accountId, '', workOrder);
            console.log('entered if block', lambdaResponse);

            const { pdfData } = lambdaResponse;
            buffer = pdfData ? Buffer.from(pdfData, 'base64') : undefined;
            pdfLink = getPdfFileNameFromLambdaResponse(lambdaResponse);
            const index = arrProjects.findIndex((item) => item.id === project.id);
            arrProjects[index].workOrderPdfLink = pdfLink;
            project.workOrderPdfLink = pdfLink;

            await editWorkOrder({ ...workOrder, dueDatePdf: dueDateForLambda, pdfLink });
          }
          if (buffer && pdfLink) {
            console.log('entered if block 2');

            await storeFile(storeId, pdfLink, buffer, Config.fileStoreUrl);
          }

          // if (workOrder.pdfLink) {
          //   console.log('entered if block 3');

          //   const response = await retrieveFile(storeId, workOrder.pdfLink, Config.fileStoreUrl);
          //   buffer = Buffer.from(response, 'base64');
          // }
          if (!workOrder.pdfLink) {
            console.log('entered if block 5');
            await editWorkOrder({ ...workOrder, pdfLink, pdfGeneratedAt: new Date() });
          }
        }

        // let lambdaResponse = await callLambda(
        //   responseForLambda,
        //   accountId,
        //   Config.fileStoreUrl,
        //   workOrder
        // );
      }
    }
    setProjectList(arrProjects);
    await print(arrProjects);
    // setCounter((prev) => prev + 1);
    // this.setState((state) => ({...state, projectList: arrProjects}), async () => {

    // })
  };

  // ####### END OF FETCH FUNCTIONS #######

  // ####### USE EFFECT #######
  useEffect(() => {
    if (!managedAccountId) {
      return;
    }
    fetchRoles();
    loadSeatUsage();
    fetchTeamData();
  }, [managedAccountId, refetchRoles]);
  // To focus new status input field

  useEffect(() => {
    loadClients();
  }, []);

  useEffect(() => {
    void fetchRoleAssignmentConfig();
  }, [fetchRoleAssignmentConfig]);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const handleAssistantClosed = () => {
      setRefetch((prev) => !prev);
      void loadClients();
    };

    window.addEventListener(QUIPA_ASSISTANT_CLOSED_EVENT, handleAssistantClosed);
    return () => window.removeEventListener(QUIPA_ASSISTANT_CLOSED_EVENT, handleAssistantClosed);
  }, []);

  useEffect(() => {
    fetchProjects();
  }, [refetch, props.location.pathname, props.location.search]);

  useEffect(() => {
    handleFetchProjectStatuses();
  }, [refetchProjectStatuses]);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside);

    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isSubmenuOpened]);

  useEffect(() => {
    if (
      openConfirmationModal &&
      openConfirmationModal.active &&
      selectedRows &&
      selectedRows.length > 0
    ) {
      let items_amount = 0;
      selectedRows.forEach((p: any) => {
        const foundProj = tableData && tableData.find((fp: any) => fp.id === p);
        if (foundProj) {
          const client = foundProj.client;
          if (client) {
            const orders = client.draperyOrders;
            if (orders && orders.length > 0) {
              items_amount += orders.length;
            }
          }
        }
      });
      setItemsAmount(items_amount);
    } else {
      setItemsAmount(0);
    }
  }, [selectedRows, openConfirmationModal.active]);

  useEffect(() => {
    setCurrentDate(() => new Date());
  }, [location?.href]);
  // ####### END OF USE EFFECT #######

  // ####### PROPS #######
  const confirmModalProps = {
    refs: confirmModalRef,
    active: openConfirmationModal.active,
    title:
      openConfirmationModal.action === 'archive' ? 'Archive Project?' : 'Please confirm the action',
    btn_actions: {
      confirm_btn: {
        disabled: false,
        action: () => handleConfirmModal(),
        title: 'Yes',
      },
      cancel_btn: {
        disabled: false,
        action: () => {
          setOpenConfirmationModal({
            active: false,
            action: '',
          });
        },
        title: 'No',
      },
    },
  };
  // ####### END OF PROPS #######

  // ####### CONSTs #######
  const isFiltersActivate =
    (filters.client.value && filters.client.value.length > 0) ||
    (filters.status.value && filters.status.value.length > 0) ||
    (filters.estimate.value && filters.estimate.value.length > 0) ||
    (filters.invoice.value && filters.invoice.value.length > 0) ||
    (canUseRoleAssignments && filters.assignee.value && filters.assignee.value.length > 0) ||
    (filters.lastViewedAt.value && filters.lastViewedAt.value.length > 0);

  const estimate_map = [
    { name: 'Not Created', value: 'Not Created' },
    { name: 'Created', value: 'Created' },
    { name: 'Exported to QB', value: 'Exported to QB' },
    { name: 'Sent to Customer', value: 'Sent to Customer' },
  ];

  const invoice_map = [
    { name: 'Not Created', value: 'Not Created' },
    { name: 'Not Paid', value: 'Not Paid' },
    { name: 'Partial', value: 'Partial' },
    { name: 'Paid', value: 'Paid' },
  ];

  const lastViewedAt_map = [
    { name: '1 week', value: '1 week' },
    { name: '1 month', value: '1 month' },
    { name: '3 months', value: '3 months' },
    { name: '6 months', value: '6 months' },
    { name: '1 year', value: '1 year' },
  ];

  const assigneeFilterItems = useMemo(
    () =>
      buildRoleAssignmentFilterOptions(
        canUseRoleAssignments ? roleAssignmentColumns : [],
        assignmentOptionsByRoleId
      ).map((option) => ({
        name: option.label,
        label: option.label,
        value: option.value,
      })),
    [canUseRoleAssignments, roleAssignmentColumns, assignmentOptionsByRoleId]
  );

  const visibleRoleAssignmentColumns = useMemo(
    () =>
      canUseRoleAssignments
        ? roleAssignmentColumns.filter((role) => roleColumnVisibility[role.id] !== false)
        : [],
    [canUseRoleAssignments, roleAssignmentColumns, roleColumnVisibility]
  );

  const isProjectsTableConfigurationLoaded =
    hasResolvedTeamSettingsAccess &&
    isColumnVisibilityLoaded &&
    (!canUseRoleAssignments || isRoleAssignmentConfigLoaded);
  const isProjectsPageLoading = loading || !isProjectsTableConfigurationLoaded;

  const activeFilters = () => {
    let count = 0;
    const filterKeys: Array<
      'client' | 'status' | 'estimate' | 'invoice' | 'assignee' | 'lastViewedAt'
    > = canUseRoleAssignments
      ? ['client', 'status', 'estimate', 'invoice', 'assignee', 'lastViewedAt']
      : ['client', 'status', 'estimate', 'invoice', 'lastViewedAt'];

    filterKeys.forEach((key) => {
      const temp = filters?.[key]?.value;
      if (temp && temp?.length > 0) {
        count++;
      }
    });

    return count;
  };

  const mobileFilters = {
    isFiltersActivate: isFiltersActivate,
    onResetAll: handleResetAllFilters,
    onApply: () => handleRefetch(),
    activeFilters: activeFilters(),
    isMobile: isMobile,
    disabled: false,
    filters: [
      {
        shadcn: true,
        filter_name: 'Clients',
        filter_key: 'client',
        filter_values: filters.client.value,
        multiple_select: false,
        items:
          clients &&
          clients.length > 0 &&
          clients.filter((i) => (filters.client.archived ? 1 : i.active)).filter((i) => !i.parent),
        archived: false,
        show_option_all: true,
        handleClearAllFilter: handleClearAllFilter,
        handleChooseFilter: handleChooseFilter,
        handleShowArchived: handleShowArchived,
      },
      {
        shadcn: true,
        filter_name: 'Status',
        filter_key: 'status',
        filter_values: filters.status.value,
        multiple_select: true,
        items: projectStatuses,
        archived: false,
        show_option_all: false,
        handleClearAllFilter: handleClearAllFilter,
        handleChooseFilter: handleChooseFilter,
        handleShowArchived: handleShowArchived,
      },
      {
        shadcn: true,
        filter_name: 'Estimate',
        filter_key: 'estimate',
        filter_values: filters.estimate.value,
        multiple_select: true,
        items: estimate_map,
        archived: false,
        show_option_all: false,
        handleClearAllFilter: handleClearAllFilter,
        handleChooseFilter: handleChooseFilter,
        handleShowArchived: handleShowArchived,
      },
      {
        shadcn: true,
        filter_name: 'Invoice',
        filter_key: 'invoice',
        filter_values: filters.invoice.value,
        multiple_select: true,
        items: invoice_map,
        archived: false,
        show_option_all: false,
        handleClearAllFilter: handleClearAllFilter,
        handleChooseFilter: handleChooseFilter,
        handleShowArchived: handleShowArchived,
      },
      ...(canUseRoleAssignments
        ? [
            {
              shadcn: true,
              filter_name: 'Assignee',
              filter_key: 'assignee',
              filter_values: filters.assignee.value,
              multiple_select: true,
              items: assigneeFilterItems,
              archived: false,
              show_option_all: false,
              handleClearAllFilter: handleClearAllFilter,
              handleChooseFilter: handleChooseFilter,
              handleShowArchived: handleShowArchived,
            },
          ]
        : []),
      {
        shadcn: true,
        filter_name: 'Last Viewed',
        filter_key: 'lastViewedAt',
        filter_values: filters.lastViewedAt.value,
        multiple_select: false,
        items: lastViewedAt_map,
        archived: false,
        show_option_all: true,
        handleClearAllFilter: handleClearAllFilter,
        handleChooseFilter: handleChooseFilter,
        handleShowArchived: handleShowArchived,
      },
    ],
  };

  const updateLocalProjectCustomColumnValue = useCallback(
    (projectId: number, columnId: CustomColumnId, value: string) => {
      setTableData((prev) =>
        prev.map((project: any) => {
          if (Number(project?.id || 0) !== Number(projectId || 0)) {
            return project;
          }

          return {
            ...project,
            customColumnsValues: writeCustomColumnValue(
              project?.customColumnsValues,
              columnId,
              value
            ),
          };
        })
      );
    },
    []
  );

  const persistProjectCustomColumnValue = useCallback(
    async (project: any, columnId: CustomColumnId, value: string) => {
      const projectId = Number(project?.id || 0);
      if (!projectId) {
        return false;
      }

      const previousCustomColumnsValues = project?.customColumnsValues;
      const nextCustomColumnsValues = writeCustomColumnValue(
        previousCustomColumnsValues,
        columnId,
        value
      );

      updateLocalProjectCustomColumnValue(projectId, columnId, value);

      try {
        const response = await updateProject(projectId, {
          ...project,
          customColumnsValues: nextCustomColumnsValues,
        });

        if (!response || response?.error) {
          throw new Error('Failed to update project custom column.');
        }

        return true;
      } catch (error) {
        console.error(error);
        showErrorToast('Failed to update custom column');
        setTableData((prev) =>
          prev.map((currentProject: any) => {
            if (Number(currentProject?.id || 0) !== projectId) {
              return currentProject;
            }

            return {
              ...currentProject,
              customColumnsValues: previousCustomColumnsValues,
            };
          })
        );
        return false;
      }
    },
    [updateLocalProjectCustomColumnValue]
  );

  const dataTableColumns = useMemo(() => {
    const roleAssignmentDataTableColumns = visibleRoleAssignmentColumns.map((role) => ({
      id: `assignment_role_${role.id}`,
      header: () => (
        <div className="w-full text-left text-[12px] font-semibold text-slate-600">
          <div className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <span>{role.name}</span>
            {showAssigneeColumnsNewBadge ? <span className="new_pill">New</span> : null}
          </div>
        </div>
      ),
      cell: ({ row }: any) => {
        const optionsByRoleId = assignmentOptionsByRoleIdRef.current;
        const options = optionsByRoleId[role.id] || [];
        const selectedValue = getRoleAssignmentSelectedOptionKey(
          row?.original?.assignments,
          role.id
        );
        const isArchived = !!row?.original?.archived;
        const isDisabled = isArchived;
        const dropdownKey = `project_${row?.original?.id}_role_${role.id}`;

        return (
          <div className="min-w-[100px]">
            <RoleAssignmentDropdown
              open={openRoleAssignmentDropdownKey === dropdownKey}
              onOpenChange={(nextOpen) => {
                setOpenRoleAssignmentDropdownKey((currentKey) => {
                  if (nextOpen) {
                    return dropdownKey;
                  }

                  return currentKey === dropdownKey ? null : currentKey;
                });
              }}
              teamMembers={teamMembersRef.current}
              assignees={assigneesWithoutLoginRef.current}
              role={role}
              value={selectedValue}
              options={options}
              disabled={isDisabled}
              isAccountAdmin={accountIsAdmin}
              dataQa={`projects_row_${row?.original?.id}_assignment_role_${role.id}_select`}
              onChange={(nextValue) => {
                const nextAssignments = applyRoleAssignmentSelection(
                  normalizeRoleAssignments(row?.original?.assignments),
                  role.id,
                  nextValue,
                  optionsByRoleId
                );
                handleUpdateProject(row?.original, 'assignments', nextAssignments);
              }}
              manageAssignments={() => handleOpenInvitePanel('team_member', role)}
              handleUpdateTeamMember={handleUpdateTeamMember}
              handleUpdateAssignee={handleUpdateAssignee}
              handleClearValue={() => {
                let clearedAssignments = row?.original?.assignments?.filter(
                  (f: any) => f?.roleId !== role.id
                );
                handleUpdateProject(row?.original, 'assignments', clearedAssignments);
              }}
            />
          </div>
        );
      },
    }));

    return [
      // TODO: Asked to be removed in task 2472
      // {
      //   id: 'select',
      //   header: ({ table }) => (
      //     <Checkbox
      //       className="peer h-[16px] w-[16px] shrink-0 border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
      //       minusIcon={selectedRows.length !== tableData.length}
      //       checked={selectedRows && selectedRows.length > 0}
      //       onCheckedChange={(val) => handleCheckAllRows()}
      //       aria-label="Select all"
      //     />
      //   ),
      //   cell: ({ row }) => (
      //     <Checkbox
      //       className="peer h-[16px] w-[16px] shrink-0 border border-primary ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground"
      //       checked={selectedRows.includes(row?.original?.id)}
      //       onCheckedChange={(val) => handleSelectRows(row?.original?.id)}
      //       aria-label="Select row"
      //     />
      //   ),
      //   enableSorting: false,
      //   enableHiding: false,
      // },
      {
        id: 'client',
        header: ({ column }) => {
          return (
            <Button
              data-qa={`projects_sort_client`}
              className="justify-start w-full text-[12px] gap-1 px-[12px] py-[8px] underline underline-offset-2"
              type="button"
              variant={'ghost'}
              onClick={() => handleSortTable('client')}
            >
              Client/Project
              <ArrowDownIcon
                style={{
                  transform: sort.position === 1 ? '' : 'rotate(180deg)',
                  visibility: sort && sort.column === 'client' ? 'visible' : 'hidden',
                }}
                size={12}
              />
            </Button>
          );
        },
        cell: ({ row }) => {
          const client = row?.original?.client;
          const client_parents = handleRecursiveParent(client);
          const clientName = (client && client.displayName) || '';
          const parentClientLabel =
            client_parents
              ?.map((c: any) => `${(c && c.displayName) || ''}${c?.parentClient ? ' / ' : ''}`)
              .join('') || '';

          return (
            <div className="w-full min-w-0 max-w-full text-left">
              <div
                className="min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                title={clientName}
              >
                <span
                  data-qa={`projects_row_${row?.original?.id}_project_client_parent_trigger_${client?.displayName}`}
                  onClick={() => {
                    handleGoToClient(client);
                  }}
                  className="client_name"
                >
                  {highlightText(clientName, debouncedSearch)}
                </span>
              </div>

              <div
                className="min-w-0 max-w-full overflow-hidden text-ellipsis whitespace-nowrap"
                title={parentClientLabel}
              >
                {client_parents &&
                  client_parents.length > 0 &&
                  client_parents.map((c: any, idx: number) => {
                    return (
                      <span
                        key={idx}
                        data-qa={`projects_row_${row?.original?.id}_project_client_children_trigger_${c?.displayName}`}
                        onClick={() => {
                          handleGoToClient(c);
                        }}
                        className="parent_client_name"
                      >
                        {highlightText((c && c.displayName) || '', debouncedSearch)}
                        {c.parentClient && ' / '}
                      </span>
                    );
                  })}
              </div>
            </div>
          );
        },
      },
      {
        id: 'lastViewedAt',
        header: ({ column }) => {
          return (
            <Button
              data-qa={`projects_sort_lastViewedAt`}
              className="justify-start w-full text-[12px] gap-1 px-[12px] py-[8px] underline underline-offset-2"
              type="button"
              variant={'ghost'}
              onClick={() => handleSortTable('lastViewedAt')}
            >
              Viewed
              <ArrowDownIcon
                style={{
                  transform: sort.position === 1 ? '' : 'rotate(180deg)',
                  visibility: sort && sort.column === 'lastViewedAt' ? 'visible' : 'hidden',
                }}
                size={12}
              />
            </Button>
          );
        },
        cell: ({ row }) => {
          const date = row?.original?.lastViewedAt ? new Date(row?.original?.lastViewedAt) : null;
          const renderDateDispaly = () => {
            // If there is no date return empty string for display
            if (!date) return '';

            // Difference in milliseconds
            const milliseconds = currentDate.getTime() - date.getTime();
            // Extraction (yy,mm,dd,hh,mm,ss)
            const seconds = Math.floor(milliseconds / 1000);
            const minutes = Math.floor(seconds / 60);
            const hours = Math.floor(minutes / 60);
            const days = Math.floor(hours / 24);
            const months = Math.floor(days / 30);
            const year = Math.floor(days / 365);

            if (year > 0) return `${year} ${year > 1 ? 'yrs.' : 'yr.'} ago`;
            if (months > 0) return `${months} ${months > 1 ? 'mos.' : 'mo.'} ago`;
            if (days > 0) return `${days} ${days > 1 ? 'days' : 'day'} ago`;
            if (hours > 0) return `${hours} ${hours > 1 ? 'hrs.' : 'hr.'} ago`;
            if (minutes > 0) return `${minutes} ${minutes > 1 ? 'mins.' : 'min.'} ago`;
            if (seconds > 0) return `just now`;
          };
          return <div>{renderDateDispaly()}</div>;
        },
      },
      {
        id: 'status',
        header: ({ column }) => {
          return (
            <Button
              data-qa={`projects_sort_status`}
              className="justify-start w-full text-[12px] gap-1 px-[12px] py-[8px] underline underline-offset-2"
              type="button"
              variant="ghost"
              onClick={() => handleSortTable('status')}
            >
              Project Status
              <ArrowDownIcon
                style={{
                  transform: sort.position === 1 ? '' : 'rotate(180deg)',
                  visibility: sort && sort.column === 'status' ? 'visible' : 'hidden',
                }}
                size={12}
              />
            </Button>
          );
        },
        cell: ({ row }) => {
          const cellProps = {
            row: row,
            debouncedSearch: debouncedSearch,
            projectStatuses: projectStatuses,
            handleUpdateProject: handleUpdateProject,
            highlightText: highlightText,
            handleStatusColor: handleStatusColor,
            handleRefetchProjectStatuses: handleRefetchProjectStatuses,
          };
          return <DropdownCell {...cellProps} />;
        },
      },
      {
        id: 'notebook',
        header: ({ column }) => {
          return (
            <Button
              type="button"
              className="justify-center w-full text-[12px] gap-1 px-[12px] py-[8px] cursor-auto"
              variant={'ghost'}
            >
              Notebook
            </Button>
          );
        },
        cell: ({ row }) => {
          return (
            <div className="w-full text-center">
              <Notebook
                row={row?.original}
                defaultTab="projects"
                notebookTabs={['projects']}
                user={props.accountUser}
                table={'projects'}
                canAccessTeamSettings={props.canAccessTeamSettings}
                updateNotebook={updateNotebook}
                closeOnOutsideClick={false}
              />
            </div>
          );
        },
      },
      {
        id: 'noOfItems',
        header: ({ column }) => {
          return (
            <Button
              data-qa={`projects_sort_no_of_items`}
              type="button"
              className="justify-center w-full text-[12px] gap-1 px-[12px] py-[8px] underline underline-offset-2"
              variant={'ghost'}
              onClick={() => handleSortTable('items_qty')}
            >
              No. of Items
              {sort && sort.column === 'items_qty' && (
                <ArrowDownIcon
                  style={{
                    transform: sort.position === 1 ? '' : 'rotate(180deg)',
                    visibility: sort && sort.column === 'items_qty' ? 'visible' : 'hidden',
                  }}
                  size={12}
                />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const client = row?.original?.client;

          const ordersQty = (client && client.draperyOrders && client.draperyOrders.length) || 0;
          return (
            <div className="text-center">
              {highlightText(ordersQty.toString(), debouncedSearch)}
            </div>
          );
        },
      },
      {
        id: 'estimate',
        header: ({ column }) => {
          return (
            <Button
              data-qa={`projects_sort_estimate`}
              className="justify-start w-full text-[12px] gap-1 px-[12px] py-[8px] underline underline-offset-2"
              type="button"
              variant="ghost"
              onClick={() => handleSortTable('estimateStatus')}
            >
              Estimate
              <ArrowDownIcon
                style={{
                  transform: sort.position === 1 ? '' : 'rotate(180deg)',
                  visibility: sort && sort.column === 'estimateStatus' ? 'visible' : 'hidden',
                }}
                size={12}
              />
            </Button>
          );
        },
        cell: ({ row }) => {
          const summary = row?.original?.estimateStatusSummary;
          const client = row?.original?.client;

          const estimateStatus = row?.original?.estimateStatus || summary?.status || 'Not Created';

          const estimateDate =
            row?.original?.estimateDate !== undefined && row?.original?.estimateDate !== ''
              ? row?.original?.estimateDate
              : row?.original?.estimateDateSent !== undefined &&
                  row?.original?.estimateDateSent !== ''
                ? row?.original?.estimateDateSent
                : summary?.statusDate || undefined;

          const comboProps = {
            row: row,
            highlightText: highlightText,
            handleStatusColor: handleStatusColor,
            handleUpdateProject: handleUpdateProject,
            debouncedSearch: debouncedSearch,
            status: estimateStatus,
            date: estimateDate,
            dateKey: 'estimateDate',
            statusKey: 'estimateStatus',
            valueKey: 'estimateStatus',
            dropdownItems: [
              { name: 'Not Created', value: 'Not Created' },
              { name: 'Created', value: 'Created' },
              { name: 'Exported to QB', value: 'Exported to QB' },
              { name: 'Sent to Customer', value: 'Sent to Customer' },
            ],
            handleChangeDate: handleChangeDate,
          };
          return (
            <div className="flex w-full min-w-0 max-w-full flex-col items-start gap-0.5 whitespace-normal">
              <ComboCell {...comboProps} />
              <EstimateStatusCell
                summary={summary}
                status={estimateStatus}
                date={estimateDate}
                estimateId={row?.original?.estimateId}
                estimateNumber={row?.original?.estimateNumber}
                qbEstimateId={row?.original?.qbEstimateId}
                qbEstimateNumber={row?.original?.qbEstimateNumber}
                canOpenEstimate={accountCanSeePricing}
                onOpenEstimate={(estimateId) => handleOpenEstimateFromStatus(estimateId, client)}
                getStatusClassName={(status) => handleStatusColor('estimateStatus', status)}
                compact
                className="w-full"
                showStatusLine={false}
                dataQaPrefix={`projects-row-${row?.original?.id || row.index}`}
              />
            </div>
          );
        },
      },
      {
        id: 'invoice',
        header: ({ column }) => {
          return (
            <Button
              data-qa={`projects_sort_invoice`}
              className="justify-start w-full text-[12px] gap-1 px-[12px] py-[8px] underline underline-offset-2"
              type="button"
              variant="ghost"
              onClick={() => handleSortTable('invoiceStatus')}
            >
              Invoice
              <ArrowDownIcon
                style={{
                  transform: sort.position === 1 ? '' : 'rotate(180deg)',
                  visibility: sort && sort.column === 'invoiceStatus' ? 'visible' : 'hidden',
                }}
                size={12}
              />
            </Button>
          );
        },
        cell: ({ row }) => {
          const client = row?.original?.client;
          const lastOrder =
            client && client.draperyOrders && client.draperyOrders.length > 0
              ? client.draperyOrders
                  .sort((a: any, b: any) => {
                    return a.id - b.id;
                  })
                  .slice(-1)[0]
              : null;

          const invoiceStatus =
            row?.original?.invoiceStatus ||
            (lastOrder &&
              lastOrder.draperyOrderSummary &&
              lastOrder.draperyOrderSummary.invoiceJson &&
              lastOrder.draperyOrderSummary.invoiceJson.paid) ||
            '';

          const invoiceDate = row?.original?.invoiceDate
            ? moment(row?.original?.invoiceDate).format('MM/DD/YY')
            : lastOrder &&
                lastOrder.draperyOrderSummary &&
                lastOrder.draperyOrderSummary.invoiceJson &&
                lastOrder.draperyOrderSummary.invoiceJson.invoiceDate
              ? moment(lastOrder.draperyOrderSummary.invoiceJson.invoiceDate).format('MM/DD/YY')
              : undefined;

          const comboProps = {
            row: row,
            highlightText: highlightText,
            handleStatusColor: handleStatusColor,
            handleUpdateProject: handleUpdateProject,
            debouncedSearch: debouncedSearch,
            status: invoiceStatus,
            date: invoiceDate,
            dateKey: 'invoiceDate',
            statusKey: 'invoicePaid',
            valueKey: 'invoiceStatus',
            dropdownItems: [
              { name: 'Not Created', value: 'Not Created' },
              { name: 'Not Paid', value: 'Not Paid' },
              { name: 'Partial', value: 'Partial' },
              { name: 'Paid', value: 'Paid' },
            ],
            handleChangeDate: handleChangeDate,
          };
          return <ComboCell {...comboProps} />;
        },
      },
      {
        id: 'installDate',
        header: ({ column }) => {
          return (
            <Button
              data-qa={`projects_sort_install_date`}
              className="justify-center w-full text-[12px] gap-1 px-[12px] py-[8px] underline underline-offset-2"
              type="button"
              variant="ghost"
              onClick={() => handleSortTable('installDate')}
            >
              Install Date
              {sort && sort.column === 'installDate' && (
                <ArrowDownIcon
                  style={{
                    transform: sort.position === 1 ? '' : 'rotate(180deg)',
                    visibility: sort && sort.column === 'installDate' ? 'visible' : 'hidden',
                  }}
                  size={12}
                />
              )}
            </Button>
          );
        },
        cell: ({ row }) => {
          const client = row?.original?.client;
          const lastOrder =
            client && client.draperyOrders && client.draperyOrders.length > 0
              ? client.draperyOrders
                  .sort((a: any, b: any) => {
                    return a.id - b.id;
                  })
                  .slice(-1)[0]
              : null;
          const installDate = row?.original?.installDate
            ? moment(row?.original?.installDate).format('MM/DD/YY')
            : lastOrder &&
                lastOrder.draperyOrderSummary &&
                lastOrder.draperyOrderSummary.installationJson &&
                lastOrder.draperyOrderSummary.installationJson.dateOfInstall
              ? moment(lastOrder.draperyOrderSummary.installationJson.dateOfInstall).format(
                  'MM/DD/YY'
                )
              : undefined;

          const tabelCellProps = {
            row: row,
            debouncedSearch: debouncedSearch,
            highlightText: highlightText,
            date: installDate,
            dateKey: 'installDate',
            handleChangeDate: handleChangeDate,
          };
          return (
            <div className="text-center">
              <DateCell {...tabelCellProps} />
            </div>
          );
        },
      },
      ...visibleCustomColumns.map((customColumn) => {
        const customColumnId = customColumn.id as CustomColumnId;
        const visibilityKey = CUSTOM_COLUMN_VISIBILITY_KEY_BY_ID[customColumnId];

        return {
          id: visibilityKey,
          header: () => (
            // <Button
            //   type="button"
            //   className="justify-start w-full text-[12px] gap-1 px-[12px] py-[8px] cursor-auto"
            //   variant="ghost"
            //   title={customColumn.name}
            // >
            //   <span className="block w-full truncate text-left">{customColumn.name}</span>
            // </Button>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="w-[150px] overflow-hidden px-[8px] py-[12px]">
                  <span className="block w-full truncate text-[12px] cursor-default">
                    {customColumn.name}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>{customColumn.name}</TooltipContent>
            </Tooltip>
          ),
          cell: ({ row }) => {
            const project = row?.original;
            const currentValue = readCustomColumnValue(
              project?.customColumnsValues,
              customColumnId
            );
            const options = customColumn.options || [];

            if (customColumn.inputType === 'dropdown') {
              return (
                <div className="min-h-[70px] px-[6px] py-0 flex items-center">
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <button
                        type="button"
                        className="inline-flex h-9 w-full items-center justify-start gap-1 px-0 text-left text-sm text-[#0f172a] bg-transparent border-0 shadow-none hover:bg-[#f8fafc] focus-visible:outline-none focus-visible:ring-0"
                      >
                        <span
                          className={
                            currentValue
                              ? 'truncate text-foreground'
                              : 'truncate text-muted-foreground'
                          }
                        >
                          {currentValue || 'Select'}
                        </span>
                        <LucideChevronDown className="h-4 w-4 shrink-0 text-slate-500" />
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      align="start"
                      sideOffset={4}
                      className="min-w-[180px] max-w-[260px] max-h-[340px] overflow-y-auto rounded-[8px] border border-[#d5dde5] bg-white p-1 shadow-[0_10px_28px_rgba(15,23,42,0.15)] z-[999]"
                    >
                      {currentValue ? (
                        <>
                          <DropdownMenuItem
                            className="text-muted-foreground italic cursor-pointer"
                            onSelect={() => {
                              void persistProjectCustomColumnValue(project, customColumnId, '');
                            }}
                          >
                            Clear selection
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                        </>
                      ) : null}
                      {options.map((option) => (
                        <DropdownMenuItem
                          key={`${customColumnId}-${option}`}
                          className="cursor-pointer text-gray-500"
                          onSelect={() => {
                            if (option === currentValue) return;
                            void persistProjectCustomColumnValue(project, customColumnId, option);
                          }}
                        >
                          {option}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              );
            }

            return (
              <div className="min-h-[70px] w-full min-w-0 px-[6px] py-0 flex items-center overflow-hidden">
                <div className="w-full min-w-0 max-w-[158px]">
                  <CustomColumnTextCell
                    value={currentValue}
                    onCommit={async (nextValue) => {
                      await persistProjectCustomColumnValue(project, customColumnId, nextValue);
                    }}
                  />
                </div>
              </div>
            );
          },
        };
      }),
      ...roleAssignmentDataTableColumns,
      {
        id: 'actions',
        enableHiding: false,
        cell: ({ row }) => {
          const dotMenuProps = {
            row: row,
            handleGoToClient: handleGoToClient,
            fetchClientProjects: fetchClientProjects,
            handleOpenConfirmModal: handleOpenConfirmModal,
            disabled: !accountCanSeePricing,
          };
          return <DotMenuCell {...dotMenuProps} />;
        },
      },
    ];
  }, [
    debouncedSearch,
    projectStatuses,
    handleUpdateProject,
    visibleRoleAssignmentColumns,
    visibleCustomColumns,
    persistProjectCustomColumnValue,
    accountCanSeePricing,
    handleOpenEstimateFromStatus,
    accountIsAdmin,
    openRoleAssignmentDropdownKey,
    props.accountUser,
  ]);

  const columnFilterProps = {
    value: columnVisibility,
    onChange: handleColumnVisibilityChange,
    showCustomFieldsSection: true,
    canCustomizeCustomColumns,
    initialCustomColumns: customColumnsConfig,
    onSaveCustomColumns: handleSaveCustomColumns,
    hiddenKeys: hiddenColumnKeys,
  };
  // ####### END OF CONSTs #######

  return (
    <>
      <div className="projects_page_container md:m-[24px] m-[12px]">
        <header className="projects_header">
          <div className="projects_row md:gap-[10px] gap-[5px]">
            <div className="projects_title">
              <span className="md:text-[24px] text-[16px]">Projects ({tableData.length})</span>
            </div>

            <div ref={submenuRef} className="projects_actions">
              {accountCanSeePricing && (
                <div>
                  <Button
                    data-qa={`projects_add_item_to_project`}
                    variant={'default'}
                    type="button"
                    onClick={() => setIsSubmenuOpened(true)}
                  >
                    <AddIcon />
                    <span>Add Item to Project</span>
                  </Button>
                </div>
              )}

              {accountCanSeePricing && isSubmenuOpened && (
                <div className="orders_sub_menu">
                  <div style={{ background: 'white' }}>
                    <button
                      data-qa={`projects_add_item_to_project_drapery`}
                      onClick={() => handleNewClientProject('drapery-order')}
                    >
                      Drapery
                    </button>

                    <button
                      data-qa={`projects_add_item_to_project_roman_shade`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      onClick={() => handleNewClientProject('roman-shade-order')}
                    >
                      <span style={{ whiteSpace: 'nowrap' }}>Roman Shade</span>
                    </button>

                    <button
                      data-qa={`projects_add_item_to_project_purchased_goods`}
                      type="button"
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}
                      onClick={() => props.handleShowBottomSheet(true)}
                    >
                      <span>Purchased Goods</span>
                      {showNewBadge ? <span className="new_pill">New</span> : null}
                    </button>

                    <button
                      data-qa={`projects_add_item_to_project_custom_order`}
                      onClick={() => handleNewClientProject('custom-order')}
                    >
                      Other
                    </button>

                    {checkGate(QUIPA_ASSISTANT_CREATE_PROJECT_GATE) && (
                      <>
                        <div className="orders_sub_menu_divider" />
                        <button
                          data-qa={`projects_add_item_to_project_assistant`}
                          className="assistant_option"
                          onClick={handleOpenAssistant}
                        >
                          Start New Project with QUIPA Assistant
                        </button>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* <div className="filters_container justify-start"> */}
          <div className="flex items-center gap-2 flex-wrap md:py-[16px] py-[8px]">
            {!isProjectsTableConfigurationLoaded ? (
              <div className="h-9 w-full" />
            ) : (
              <>
                <InputGroup className="max-w-xs inline-flex">
                  <InputGroupInput
                    data-qa={`projects_search_input`}
                    ref={searchInputRef}
                    value={searchTerm || ''}
                    onChange={(e) => handleChangeValueSearchInput(e)}
                    onKeyDown={handleSearchInputKeyDown}
                    placeholder="Search..."
                  />
                  <InputGroupAddon>
                    {searchTerm ? (
                      <CirclePlus
                        data-qa={`projects_search_input_clear`}
                        onMouseDown={() => {
                          handleClearSearchKeyword();
                        }}
                        stroke="white"
                        fill="#3b87a0"
                        className="!size-5 text-lg rotate-[45deg] cursor-pointer"
                      />
                    ) : (
                      <Search />
                    )}
                  </InputGroupAddon>
                </InputGroup>

                {isMobile ? (
                  <>
                    <FiltersDrawer {...mobileFilters} />
                  </>
                ) : (
                  <>
                    <Filter
                      shadcn
                      filter_name={'Clients'}
                      filter_key={'client'}
                      filter_values={filters.client.value}
                      multiple_select={false}
                      items={
                        clients &&
                        clients.length > 0 &&
                        clients
                          .filter((i) => (filters.client.archived ? 1 : i.active))
                          .filter((i) => !i.parent)
                      }
                      archived={false}
                      show_option_all={true}
                      handleClearAllFilter={handleClearAllFilter}
                      handleChooseFilter={handleChooseFilter}
                      handleShowArchived={handleShowArchived}
                    />

                    <Filter
                      shadcn
                      filter_name={'Status'}
                      filter_key={'status'}
                      filter_values={filters.status.value}
                      multiple_select={true}
                      items={projectStatuses}
                      archived={false}
                      show_option_all={false}
                      handleClearAllFilter={handleClearAllFilter}
                      handleChooseFilter={handleChooseFilter}
                      handleShowArchived={handleShowArchived}
                    />

                    <Filter
                      shadcn
                      filter_name={'Estimate'}
                      filter_key={'estimate'}
                      filter_values={filters.estimate.value}
                      multiple_select={true}
                      items={estimate_map}
                      archived={false}
                      show_option_all={false}
                      handleClearAllFilter={handleClearAllFilter}
                      handleChooseFilter={handleChooseFilter}
                      handleShowArchived={handleShowArchived}
                    />

                    <Filter
                      shadcn
                      filter_name={'Invoice'}
                      filter_key={'invoice'}
                      filter_values={filters.invoice.value}
                      multiple_select={true}
                      items={invoice_map}
                      archived={false}
                      show_option_all={false}
                      handleClearAllFilter={handleClearAllFilter}
                      handleChooseFilter={handleChooseFilter}
                      handleShowArchived={handleShowArchived}
                    />

                    {canUseRoleAssignments && (
                      <Filter
                        shadcn
                        filter_name={'Assignee'}
                        filter_key={'assignee'}
                        filter_values={filters.assignee.value}
                        multiple_select={true}
                        items={assigneeFilterItems}
                        archived={false}
                        show_option_all={false}
                        handleClearAllFilter={handleClearAllFilter}
                        handleChooseFilter={handleChooseFilter}
                        handleShowArchived={handleShowArchived}
                      />
                    )}

                    <Filter
                      shadcn
                      filter_name={'Last Viewed'}
                      filter_key={'lastViewedAt'}
                      filter_values={filters.lastViewedAt.value}
                      multiple_select={false}
                      items={lastViewedAt_map}
                      archived={false}
                      show_option_all={true}
                      handleClearAllFilter={handleClearAllFilter}
                      handleChooseFilter={handleChooseFilter}
                      handleShowArchived={handleShowArchived}
                    />

                    {isFiltersActivate && (
                      <div className="reset_all_filters">
                        <div className="reset_all_filters_button_container">
                          <Button
                            data-qa="projects_clear_all_filters"
                            variant={'resetFilters'}
                            type="button"
                            onClick={() => handleResetAllFilters()}
                          >
                            <LucideX />
                            Clear All
                          </Button>
                        </div>
                      </div>
                    )}
                  </>
                )}

                <ColumnVisibilityMenu
                  {...columnFilterProps}
                  // disabledKeys={['estimate']}
                  preserveOnHideAll={['client', 'estimate']}
                  roleAssignmentColumns={roleAssignmentColumns}
                  roleColumnVisibility={roleColumnVisibility}
                  onRoleColumnVisibilityChange={handleRoleColumnVisibilityChange}
                  isAccountAdmin={accountIsAdmin}
                  account={account}
                  handleRefetchRoles={handleRefetchRoles}
                  teamMembers={teamMembers}
                  assignees={assigneesWithoutLogin}
                  handleUpdateTeamMember={handleUpdateTeamMember}
                  handleUpdateAssignee={handleUpdateAssignee}
                  unsetRoleColumns={unsetRoleColumns}
                  handleOpenInvitePanel={handleOpenInvitePanel}
                  canAccessTeamSettings={props.canAccessTeamSettings}
                />
              </>
            )}
          </div>
        </header>

        {isProjectsPageLoading ? (
          <FadeLoader
            data-qa={'loading_spinner'}
            style={{
              position: 'absolute',
              top: '50%',
              left: '50%',
              transform: 'translate(50%,-50%)',
              zIndex: 9999,
              display: !isProjectsPageLoading ? 'none' : 'block',
            }}
            color="#3B97B1"
            aria-label="Loading Spinner"
            data-testid="loader"
          />
        ) : (
          <DataTable
            handleResetAllFilters={handleResetAllFilters}
            columns={dataTableColumns}
            data={tableData}
            isFiltersActivate={isFiltersActivate}
            columnVisibility={columnVisibility}
          />
        )}

        <ConfirmModal {...confirmModalProps}>
          {openConfirmationModal.action === 'archive' ? (
            <span>
              This will {openConfirmationModal.action}{' '}
              <b>
                {itemsAmount} {itemsAmount <= 1 ? 'item' : 'items'}
              </b>{' '}
              in {selectedRows.length <= 1 ? 'this project' : 'these projects'}.
            </span>
          ) : (
            <span>
              Are you sure you want to {openConfirmationModal.action} {selectedRows.length}{' '}
              {selectedRows.length <= 1 ? 'item' : 'items'}?
            </span>
          )}
        </ConfirmModal>
        {generatePdf && (
          <GeneratePdfSpinner>
            <FadeLoader
              style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(50%,-50%)',
                zIndex: 100000000,
                display: !generatePdf ? 'none' : 'block',
              }}
              color="#3B97B1"
              aria-label="Loading Spinner"
              data-testid="loader"
            />
          </GeneratePdfSpinner>
        )}
        <PrintModal
          selectedItemsNumber={projectList.length}
          preparedItemsNumber={projectList.filter((p) => p.hasOwnProperty('workOrderId')).length}
          accountId={Number(localStorage.getItem('accountId'))}
          isModalOpen={isPrintModalOpen}
          handlePrint={handlePrint}
          //  key={'estimate-' + client.id}
          //  estimateId={this.state.qbEstimateId || ''}
          //  qbEstimateDocNumber={this.state.qbEstimateDocNumber || ''}
          onClose={() => setIsPrintModalOpen(false)}
        ></PrintModal>
      </div>

      <EstimateBuilderSheet
        open={estimateSheetState.open}
        initialMode="view"
        estimateId={estimateSheetState.estimateId}
        clientId={estimateSheetState.client?.id}
        accountId={accountId}
        client={estimateSheetState.client}
        account={account}
        accountUser={props.accountUser}
        qboConnected={estimateSheetState.qboConnected}
        existingEstimates={estimateSheetState.estimates}
        onClose={handleCloseEstimateSheet}
        onChanged={handleEstimateSheetChanged}
        singleUser={!props.canAccessTeamSettings}
      />

      {canManageTeamMembers ? (
        <WideSheet
          open={isInvitePanelOpen}
          onCancel={handleCloseInvitePanel}
          onSave={handleSubmitInvitePanel}
          title="Add"
          subTitle={
            invitePanelMode === 'team_member'
              ? 'Invite a team member with login access'
              : 'Add an assignee without login'
          }
          saveLabel={
            invitePanelMode === 'team_member'
              ? isInvitingMember
                ? 'Sending...'
                : 'Send Invitation'
              : 'Add Assignee'
          }
          cancelLabel="Cancel"
          saveDisabled={!canSubmitInvitePanel}
          overlayDataQa="team-settings-invite-panel-overlay-close"
          overlayStyle={{
            backgroundColor: 'rgba(0, 0, 0, 0.78)',
          }}
          closeButtonDataQa="team-settings-invite-panel-close-button"
          cancelButtonDataQa="team-settings-invite-panel-cancel"
          saveButtonDataQa="team-settings-invite-panel-submit"
        >
          <div>
            <div className="grid grid-cols-2 overflow-hidden rounded-[8px] border border-[#D8E0E8]">
              <button
                type="button"
                className={`border-0 py-3 text-[14px] font-semibold ${
                  invitePanelMode === 'team_member'
                    ? 'bg-[#3B97B1] text-white'
                    : 'bg-white text-[#64748B]'
                }`}
                onClick={() => handleSwitchInviteMode('team_member')}
                data-qa="team-settings-invite-panel-mode-team-member"
              >
                Add Team Member
              </button>
              <button
                type="button"
                className={`border-0 py-3 text-[14px] font-semibold ${
                  invitePanelMode === 'assignee'
                    ? 'bg-[#3B97B1] text-white'
                    : 'bg-white text-[#64748B]'
                }`}
                onClick={() => handleSwitchInviteMode('assignee')}
                data-qa="team-settings-invite-panel-mode-assignee"
              >
                Add Assignee
              </button>
            </div>

            <div className="mt-5 rounded-[8px] border border-[#D8E0E8] bg-[#f1f5f980] px-4 py-4">
              <div className="flex items-center gap-3">
                {invitePanelMode === 'team_member' ? (
                  <UserCheckIcon className="h-5 w-5 text-[#64748B]" />
                ) : (
                  <ClipboardList className="h-5 w-5 text-[#64748B]" />
                )}
                <h3 className="m-0 text-[14px] font-semibold text-[#111827]">
                  {invitePanelMode === 'team_member' ? 'Team Member' : 'Assignees without login'}
                </h3>
              </div>
              <ul className="m-0 mt-3 list-outside list-disc pl-14 text-[14px] text-[#60778F]">
                {invitePanelMode === 'team_member' ? (
                  <>
                    <li>Creates a Quipa user with login access</li>
                    <li>Can log in and view assigned work</li>
                    <li>Can collaborate with the team</li>
                    <li>Receives notifications and @mentions</li>
                    <li>Uses a paid seat</li>
                  </>
                ) : (
                  <>
                    <li>People you assign work to who do not have Quipa access</li>
                    <li>Appear in assignee lists</li>
                    <li>Do not receive notifications or @mentions</li>
                  </>
                )}
              </ul>
            </div>

            {invitePanelMode === 'team_member' ? (
              <>
                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">
                    Contact Information
                  </h3>
                  <label className="mt-4 block text-[14px] font-semibold text-[#111827]">
                    Email
                  </label>
                  <div className="relative mt-2">
                    <Mail className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60778F]" />
                    <Input
                      type="email"
                      placeholder="teammate@company.com"
                      value={memberEmail}
                      onChange={(e) => setMemberEmail(e.target.value)}
                      className="h-11 pl-9"
                      data-qa="team-settings-invite-member-email-input"
                    />
                  </div>
                </div>

                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Roles</h3>
                  <p className="mb-0 mt-4 text-[14px] text-[#60778F]">
                    Assign work roles to describe what type of work this person does.
                  </p>
                  <div className="mt-4 rounded-[8px] border border-[#D8E0E8] bg-white px-3 py-3">
                    {roles.length === 0 ? (
                      <p className="m-0 text-[14px] text-[#64748B]">
                        No roles created yet. Add roles in the Roles tab first.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {roles.map((role) => (
                          <label
                            key={`member-role-${role.id}`}
                            className="flex items-center gap-3 text-[14px] font-semibold text-[#111827]"
                            data-qa={`team-settings-invite-member-role-${role.id}-label`}
                          >
                            <Checkbox
                              checked={memberSelectedRoleIds.includes(role.id)}
                              onCheckedChange={(checked) =>
                                toggleMemberRole(role.id, checked === true)
                              }
                              data-qa={`team-settings-invite-member-role-${role.id}-checkbox`}
                            />
                            <span>{role.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Permissions</h3>
                  <div className="mt-4 flex flex-col gap-3">
                    <div className="relative inline-flex w-fit items-center gap-3 text-[14px] text-[#111827]">
                      <Checkbox
                        checked={memberPricingAccess}
                        onCheckedChange={(checked) => setMemberPricingAccess(checked === true)}
                        data-qa="team-settings-invite-member-pricing-access-checkbox"
                      />
                      <span>Pricing access</span>
                      <PermissionHelpTooltip
                        tooltipKey="pricing"
                        enabled={isInvitePanelOpen}
                        open={openPermissionTooltip === 'pricing'}
                        onOpenChange={(nextOpen) =>
                          setOpenPermissionTooltip(nextOpen ? 'pricing' : null)
                        }
                        ariaLabel="Show pricing access help"
                        content="View and edit item details, construction settings, and My Shop entries."
                        width={290}
                        triggerDataQa="team-settings-invite-member-pricing-access-help"
                        contentDataQa="team-settings-invite-member-pricing-access-help-content"
                      />
                    </div>
                    <div className="relative inline-flex w-fit items-center gap-3 text-[14px] text-[#111827]">
                      <Checkbox
                        checked={memberAdminAccess}
                        onCheckedChange={(checked) => setMemberAdminAccess(checked === true)}
                        data-qa="team-settings-invite-member-admin-access-checkbox"
                      />
                      <span>Administration access</span>
                      <PermissionHelpTooltip
                        tooltipKey="admin"
                        enabled={isInvitePanelOpen}
                        open={openPermissionTooltip === 'admin'}
                        onOpenChange={(nextOpen) =>
                          setOpenPermissionTooltip(nextOpen ? 'admin' : null)
                        }
                        ariaLabel="Show administration access help"
                        content="Manage account settings, users and roles, integrations, and workspace configuration."
                        width={320}
                        triggerDataQa="team-settings-invite-member-admin-access-help"
                        contentDataQa="team-settings-invite-member-admin-access-help-content"
                      />
                    </div>
                  </div>
                </div>

                {isSeatLimitReached ? (
                  <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                    <div className="flex items-center gap-2">
                      <TriangleAlert className="h-5 w-5 text-[#F59E0B]" />
                      <h3 className="m-0 text-[16px] font-semibold text-[#C66A00]">
                        Seat Limit Reached
                      </h3>
                    </div>
                    <p className="m-0 mt-4 text-[14px] text-[#60778F]">
                      You have reached the seat limit for your account.{' '}
                      <button
                        type="button"
                        className="inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                        onClick={() => handleOpenSeatsModal(true)}
                        data-qa="team_settings_add_seats_button_link"
                      >
                        Add more seats
                      </button>{' '}
                      or{' '}
                      <button
                        type="button"
                        className="inline cursor-pointer border-0 bg-transparent p-0 text-[#2F8FB0] hover:text-[#267990]"
                        onClick={() => {
                          void handleChangePlan();
                        }}
                        data-qa="team-settings-invite-seat-limit-upgrade-plan"
                      >
                        Upgrade your plan
                      </button>
                    </p>
                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <Dialog
                        open={openSeatsModal}
                        onOpenChange={(val) => handleOpenSeatsModal(val)}
                      >
                        <DialogTrigger asChild>
                          <Button
                            type="button"
                            variant={'default'}
                            className="min-h-[36px]"
                            // onClick={() => handleOpenSeatsModal(true)}
                            // disabled={false}
                            data-qa="team_settings_add_seats_button"
                          >
                            Add Seats
                          </Button>
                        </DialogTrigger>
                        <DialogContent className="max-w-[700px]">
                          <DialogHeader>
                            <DialogTitle className="m-0 text-[24px]">Buy Extra Seats</DialogTitle>
                          </DialogHeader>

                          <div className="flex flex-col gap-4">
                            <div className="md:text-start text-center md:ml-4">
                              <RadioGroup
                                disabled={seatLoading}
                                className="flex items-center gap-1 rounded-md w-max"
                                value={seatState.interval}
                                onValueChange={(val) => handleChangeInterval(val)}
                              >
                                <div className="">
                                  <RadioGroupItem
                                    value={'month'}
                                    id="seat_monthly"
                                    className="hidden"
                                  />
                                  <Label
                                    data-loading={seatLoading}
                                    data-checked={seatState.interval === 'month'}
                                    htmlFor="seat_monthly"
                                    className="transition duration-300 cursor-pointer px-[12px] py-[8px] rounded-md data-[checked=true]:shadow-[0_0_0_2px_rgba(59,151,177,1)] border border-input data-[checked=false]:bg-white data-[loading=true]:opacity-50"
                                  >
                                    Monthly
                                  </Label>
                                </div>
                                <div className="">
                                  <RadioGroupItem
                                    value={'year'}
                                    id="seat_yearly"
                                    className="hidden"
                                  />
                                  <Label
                                    data-loading={seatLoading}
                                    data-checked={seatState.interval === 'year'}
                                    htmlFor="seat_yearly"
                                    className="transition duration-300 cursor-pointer px-[12px] py-[8px] rounded-md data-[checked=true]:shadow-[0_0_0_2px_rgba(59,151,177,1)] border border-input data-[checked=false]:bg-white data-[loading=true]:opacity-50"
                                  >
                                    Yearly
                                  </Label>
                                </div>
                              </RadioGroup>
                            </div>

                            <div className="p-[24px] rounded-[12px] border border-2 border-primary_dark">
                              <div className="flex md:flex-row flex-col justify-between gap-2">
                                <div>
                                  <div className="mb-4 flex items-center gap-2">
                                    <div>
                                      <Input
                                        disabled={seatLoading}
                                        onChange={(e) => handleChangeSeatQty(e)}
                                        className="px-1 text-center max-w-[60px] min-h-[10px] max-h-max"
                                        value={seatState.quantity}
                                        autoFocus
                                      />
                                    </div>
                                    <span className="text-[18px] font-medium">
                                      Extra Seat{Number(seatState.quantity) > 1 ? 's' : ''}
                                    </span>
                                  </div>

                                  <div>
                                    <span>
                                      $
                                      {(
                                        (+seatState.cost || 0) * (+seatState.quantity || 0)
                                      ).toFixed(2)}{' '}
                                      per {seatState.interval}
                                    </span>
                                  </div>
                                </div>

                                <div className="flex items-center">
                                  <div className="flex items-center gap-2 px-[16px] py-[8px] border border-input rounded-md">
                                    <CircleCheck className="w-[16px] h-[16px] text-primary" />
                                    <span>Selected</span>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </div>

                          <DialogFooter>
                            <DialogClose>
                              <Button type="button" variant={'outline'}>
                                Close
                              </Button>
                            </DialogClose>
                            <Button
                              disabled={seatLoading || !seatState.quantity}
                              onClick={() => handleBuyMoreSeats()}
                              type="button"
                            >
                              Buy
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>

                      <Button
                        variant={'outline'}
                        type="button"
                        className="font-medium min-h-[36px]"
                        onClick={() => {
                          void handleRemoveInactiveMembers();
                        }}
                        disabled={isRemovingInactiveMembers || isInvitationActionDisabled()}
                        data-qa="team-settings-invite-seat-limit-remove-inactive-members"
                      >
                        {isRemovingInactiveMembers ? 'Removing...' : 'Remove inactive members'}
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                    <div className="flex items-center gap-3">
                      <CreditCard className="h-5 w-5 text-[#64748B]" />
                      <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Seat Usage</h3>
                    </div>
                    <p className="mb-0 mt-4 text-[14px] text-[#111827]">
                      Current: {usedSlots} of {availableSlots} seats used
                    </p>
                    <div className="mt-3 h-[8px] rounded-full bg-[#D7E2E9]">
                      <div
                        className="h-full rounded-full bg-[#3B97B1]"
                        style={{ width: `${seatUsagePercent}%` }}
                      />
                    </div>
                    <p className="m-0 mt-4 text-[14px] text-[#60778F]">
                      Sending this invitation will use 1 seat. You will have{' '}
                      {remainingSeatsAfterInvite} seats remaining.
                    </p>
                  </div>
                )}
              </>
            ) : (
              <>
                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">
                    Assignee Information
                  </h3>
                  <label className="mt-4 block text-[14px] font-semibold text-[#111827]">
                    Name *
                  </label>
                  <div className="relative mt-2">
                    <UserRound className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60778F]" />
                    <Input
                      type="text"
                      placeholder="John Smith"
                      value={assigneeName}
                      onChange={(e) => handleAssigneeNameChange(e.target.value)}
                      className="h-11 pl-9"
                      data-qa="team-settings-invite-assignee-name-input"
                    />
                  </div>

                  <label className="mt-4 block text-[14px] font-semibold text-[#111827]">
                    Label * <span className="font-normal text-[#60778F]">(1-4 chars)</span>
                  </label>
                  <div className="relative mt-2">
                    <AtSign className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#60778F]" />
                    <Input
                      type="text"
                      placeholder="JS"
                      value={assigneeLabel}
                      onChange={(e) => handleAssigneeLabelChange(e.target.value)}
                      className="h-11 pl-9"
                      data-qa="team-settings-invite-assignee-label-input"
                    />
                  </div>
                  <p className="m-0 mt-2 text-[14px] text-[#60778F]">Used for @mentions</p>
                </div>

                <div className="mt-5 rounded-[8px] bg-[#f1f5f980] px-4 py-4">
                  <h3 className="m-0 text-[16px] font-semibold text-[#111827]">Roles *</h3>
                  <p className="mb-0 mt-4 text-[14px] text-[#60778F]">
                    Select at least one role to describe what type of work this person does.
                  </p>
                  <div className="mt-4 rounded-[8px] border border-[#D8E0E8] bg-white px-3 py-3">
                    {roles.length === 0 ? (
                      <p className="m-0 text-[14px] text-[#64748B]">
                        No roles created yet. Add roles in the Roles tab first.
                      </p>
                    ) : (
                      <div className="flex flex-col gap-3">
                        {roles.map((role) => (
                          <label
                            key={`assignee-role-${role.id}`}
                            className="flex items-center gap-3 text-[14px] font-semibold text-[#111827]"
                            data-qa={`team-settings-invite-assignee-role-${role.id}-label`}
                          >
                            <Checkbox
                              checked={assigneeSelectedRoleIds.includes(role.id)}
                              onCheckedChange={(checked) =>
                                toggleAssigneeRole(role.id, checked === true)
                              }
                              data-qa={`team-settings-invite-assignee-role-${role.id}-checkbox`}
                            />
                            <span>{role.name}</span>
                          </label>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>
        </WideSheet>
      ) : null}
    </>
  );
};

export default ProjectsPage;
